/**
 * Expense Tracker Module
 * Handles quick expense entry, duplicate detection, and budget progress tracking
 */

import { getCurrentUser } from './auth.js';
import {
  getCurrentBudgetData,
  getCurrentBudgetId,
  getBudgetVersions,
  getRecurringExpenses,
  getCurrentMonthRecurringExpenses,
  getCurrentVersionName
} from './budget.js';
import { getLinkingState } from './linkSharing.js';
import { db } from './firebase.js';
import { getFirestore, collection, doc, addDoc, query, where, orderBy, limit, getDocs, getDoc, updateDoc, deleteDoc, Timestamp } from 'firebase/firestore';
import { formatCurrency } from './utils/currency.js';
import { trackExpenseLogging } from './achievements.js';
import { updateMonthlyProgress } from './visualizations.js';

// Debounce timer for monthly progress updates
let monthlyProgressUpdateTimer = null;

// In-memory cache for current-month expense lists.
// All per-category, per-lineItem, and monthly total queries are computed from
// this single fetch rather than issuing separate Firestore queries.
const monthExpenseCache = new Map();
const MONTH_EXPENSE_CACHE_DURATION = 30000; // 30 seconds

async function getCachedMonthExpenses(budgetId, month) {
  const cacheKey = `${budgetId}:${month}`;
  const cached = monthExpenseCache.get(cacheKey);
  if (cached && Date.now() - cached.timestamp < MONTH_EXPENSE_CACHE_DURATION) {
    return cached.expenses;
  }

  const q = query(
    getUserExpenseCollection(),
    where('budgetId', '==', budgetId),
    where('month', '==', month)
  );
  const snapshot = await getDocs(q);
  const expenses = [];
  snapshot.forEach(doc => expenses.push({ id: doc.id, ...doc.data() }));

  monthExpenseCache.set(cacheKey, { expenses, timestamp: Date.now() });
  return expenses;
}

function clearMonthExpenseCache() {
  monthExpenseCache.clear();
}

// Register with the global cache-clearing hook used by budget/expenseTracker mutations
if (typeof window !== 'undefined') {
  const _prevClear = window.clearExpenseCache;
  window.clearExpenseCache = function () {
    clearMonthExpenseCache();
    if (_prevClear) _prevClear();
  };
}

/**
 * Get the user-scoped expense collection reference
 * Uses new user-scoped structure: users/{userId}/expenses
 * For linked accounts, uses the shared account's expense collection
 */
function getUserExpenseCollection() {
  const user = getCurrentUser();
  if (!user) {
    throw new Error('No user authenticated');
  }
  
  // Check if user is linked to another account
  const linkState = getLinkingState();
  const targetUserId = linkState.isLinkedAccount ? linkState.linkedAccountId : user.uid;
  
  
  return collection(db, `users/${targetUserId}/expenses`);
}

/**
 * Safely evaluate math expressions
 * Supports basic operations: +, -, *, /, (, )
 * Returns null if invalid expression
 */
function evaluateMathExpression(expression) {
  if (!expression || typeof expression !== 'string') return null;
  
  // Remove spaces and validate characters (only allow numbers, operators, parentheses, decimal points)
  const cleanExpression = expression.replace(/\s/g, '');
  if (!/^[0-9+\-*/().]+$/.test(cleanExpression)) return null;
  
  // Prevent division by zero and other invalid patterns
  if (/\/0(?!\.)/.test(cleanExpression)) return null;
  if (/[+\-*/]{2,}/.test(cleanExpression)) return null; // No consecutive operators
  if (/^[+\-*/]/.test(cleanExpression) && !/^[-]/.test(cleanExpression)) return null; // No leading operators except minus
  if (/[+\-*/]$/.test(cleanExpression)) return null; // No trailing operators
  
  try {
    // Use Function constructor for safe evaluation (better than eval)
    const result = Function(`"use strict"; return (${cleanExpression})`)();
    
    // Validate result is a finite number
    if (typeof result !== 'number' || !isFinite(result)) return null;
    
    return Math.round(result * 100) / 100; // Round to 2 decimal places
  } catch (error) {
    return null;
  }
}

/**
 * Insert a math operator at the cursor position in the amount input.
 * Used by the mobile operator bar. `(` inserts `(` and positions for closing.
 */
export function insertAmountOperator(op) {
  const input = document.getElementById('expense-amount-input');
  if (!input) return;
  const start = input.selectionStart;
  const end = input.selectionEnd;
  const val = input.value;
  if (op === '(') {
    input.value = val.slice(0, start) + '()' + val.slice(end);
    input.setSelectionRange(start + 1, start + 1);
  } else {
    input.value = val.slice(0, start) + op + val.slice(end);
    input.setSelectionRange(start + 1, start + 1);
  }
  input.focus();
  input.dispatchEvent(new Event('input', { bubbles: true }));
}

/**
 * Delete the character before the cursor in the amount input.
 * Used by the mobile operator bar backspace button.
 */
export function deleteAmountChar() {
  const input = document.getElementById('expense-amount-input');
  if (!input) return;
  const start = input.selectionStart;
  const end = input.selectionEnd;
  const val = input.value;
  if (start !== end) {
    input.value = val.slice(0, start) + val.slice(end);
    input.setSelectionRange(start, start);
  } else if (start > 0) {
    input.value = val.slice(0, start - 1) + val.slice(start);
    input.setSelectionRange(start - 1, start - 1);
  }
  input.focus();
  input.dispatchEvent(new Event('input', { bubbles: true }));
}

/**
 * Parse amount input with math expression support
 */
function parseAmountInput(value) {
  if (!value || value === '') return 0;
  
  // First try to evaluate as math expression
  const mathResult = evaluateMathExpression(value.toString());
  if (mathResult !== null) {
    return mathResult;
  }
  
  // Fall back to regular parsing
  return parseFloat(value) || 0;
}

/**
 * Expense Tracker State
 */
let expenseTrackerState = {
  currentBudgetData: null,
  monthlyTotals: null,
  selectedCategory: null,
  selectedLineItem: null,
  recentExpenses: [],
  upcomingRecurring: []
};

/**
 * Frequency conversion utilities
 */
const FREQUENCY_MULTIPLIERS = {
  'monthly': 1,
  'weekly': 4.33, // 52 weeks ÷ 12 months
  'bi-weekly': 26/12, // 26 bi-weekly periods ÷ 12 months = 2.1667
  'annual': 1/12,
  'one-time': 0 // One-time items don't contribute to monthly totals
};

function convertToMonthlyAmount(amount, frequency) {
  const multiplier = FREQUENCY_MULTIPLIERS[frequency] || 1;
  return amount * multiplier;
}

/**
 * Format date to local YYYY-MM-DD string (avoiding timezone issues)
 */
function formatDateToLocal(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Initialize expense tracker when view is shown
 */
export async function initializeExpenseTracker() {
  console.log('Initializing expense tracker...');
  
  try {
    const user = getCurrentUser();
    if (!user) {
      console.error('No authenticated user for expense tracker');
      return;
    }

    // Load current budget data
    expenseTrackerState.currentBudgetData = getCurrentBudgetData();
    
    console.log('Current budget data:', expenseTrackerState.currentBudgetData);
    
    if (!expenseTrackerState.currentBudgetData) {
      console.error('No budget data available for expense tracker');
      showError('Please select a budget first');
      return;
    }

    // Initialize UI components
    await Promise.all([
      updateOverallProgress(),
      populateCategoryDropdown(),
      loadUpcomingRecurringExpenses(),
      setupEventListeners()
    ]);

    // Initialize affordability indicator to show correct state on load
    updateAffordabilityIndicator();

    console.log('Expense tracker initialized successfully');
    
  } catch (error) {
    console.error('Failed to initialize expense tracker:', error);
    showError('Failed to load expense tracker');
  }
}

/**
 * Update overall budget progress display
 */
export async function updateOverallProgress() {
  try {
    const currentMonth = new Date().toISOString().slice(0, 7); // YYYY-MM format
    const budgetId = getCurrentBudgetId();
    
    // Calculate total budgeted amount
    let totalBudgeted = 0;
    if (expenseTrackerState.currentBudgetData.categories) {
      Object.values(expenseTrackerState.currentBudgetData.categories).forEach(category => {
        if (category.items && Array.isArray(category.items)) {
          category.items.forEach(item => {
            // Convert to monthly amount based on frequency
            const monthlyAmount = convertToMonthlyAmount(item.amount || 0, item.frequency || 'monthly');
            totalBudgeted += monthlyAmount;
          });
        }
      });
    }

    // Get actual expenses for current month
    const totalSpent = await getMonthlyExpenseTotal(budgetId, currentMonth);
    
    // Get scheduled recurring expenses for current month
    const scheduledRecurring = await getScheduledRecurringTotal(currentMonth);
    
    // Calculate remaining budget
    const totalAllocated = totalSpent + scheduledRecurring;
    const remaining = totalBudgeted - totalAllocated;
    
    // Update progress bar
    const progressPercentage = totalBudgeted > 0 ? (totalSpent / totalBudgeted) * 100 : 0;
    const scheduledPercentage = totalBudgeted > 0 ? (scheduledRecurring / totalBudgeted) * 100 : 0;
    
    // Update UI elements
    updateProgressDisplay({
      totalSpent,
      scheduledRecurring,
      totalBudgeted,
      remaining,
      progressPercentage,
      scheduledPercentage,
      currentMonth
    });

  } catch (error) {
    console.error('Failed to update overall progress:', error);
  }
}

/**
 * Update progress display elements
 */
function updateProgressDisplay(data) {
  const {
    totalSpent,
    scheduledRecurring,
    totalBudgeted,
    remaining,
    progressPercentage,
    scheduledPercentage,
    currentMonth
  } = data;

  // Update month title
  const monthNames = ["January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];
  const [year, month] = currentMonth.split('-');
  const monthName = monthNames[parseInt(month) - 1];
  
  const progressTitle = document.getElementById('progress-month-title');
  if (progressTitle) {
    progressTitle.textContent = `${monthName} ${year} Journal Progress`;
  }

  // Update progress text
  const progressText = document.getElementById('progress-text');
  if (progressText) {
    progressText.textContent = `$${totalSpent.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} spent + $${scheduledRecurring.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} scheduled of $${totalBudgeted.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} budgeted`;
  }

  // Update remaining amount
  const remainingElement = document.getElementById('remaining-amount');
  if (remainingElement) {
    if (remaining < 0) {
      remainingElement.textContent = `$${Math.abs(remaining).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} over budget`;
      remainingElement.className = 'remaining-amount danger';
    } else {
      remainingElement.textContent = `$${remaining.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
      remainingElement.className = 'remaining-amount';
      
      if (remaining < totalBudgeted * 0.1) {
        remainingElement.classList.add('warning');
      }
    }
  }

  // Update progress bar with stacked display
  const progressFill = document.getElementById('overall-progress-fill');
  const scheduledFill = document.getElementById('progress-scheduled-fill');
  
  console.log('🎨 [Progress Bar Debug]:', {
    progressFill: !!progressFill,
    scheduledFill: !!scheduledFill,
    progressPercentage,
    scheduledPercentage,
    totalBudgeted,
    scheduledRecurring
  });
  
  if (progressFill) {
    // Actual spending fills first (darker color)
    progressFill.style.width = `${Math.min(progressPercentage, 100)}%`;
    progressFill.className = 'progress-fill';
    
    // Apply correct border-radius based on whether scheduled amount exists
    if (scheduledPercentage > 0) {
      progressFill.classList.add('left-rounded');
    } else {
      progressFill.classList.add('full-rounded');
    }
    
    if (progressPercentage > 100) {
      progressFill.classList.add('danger');
    } else if (progressPercentage > 80) {
      progressFill.classList.add('warning');
    }
  }

  if (scheduledFill) {
    // Scheduled expenses show as separate section (lighter color)
    const totalPercentage = progressPercentage + scheduledPercentage;
    const cappedTotal = Math.min(totalPercentage, 100);
    const cappedActual = Math.min(progressPercentage, 100);
    const scheduledWidth = cappedTotal - cappedActual;
    
    const scheduledWidthPercent = Math.max(scheduledWidth, 0);
    
    console.log('🎨 [Scheduled Fill Debug]:', {
      totalPercentage,
      cappedTotal,
      cappedActual,
      scheduledWidth,
      scheduledWidthPercent,
      leftPosition: `${cappedActual}%`
    });
    
    scheduledFill.style.setProperty('--scheduled-width', `${scheduledWidthPercent}%`);
    scheduledFill.style.width = `${scheduledWidthPercent}%`;
    scheduledFill.style.left = `${cappedActual}%`;
    scheduledFill.style.position = 'absolute';
    scheduledFill.style.height = '100%';
    scheduledFill.className = 'progress-scheduled';
    
    // Apply correct border-radius based on whether spent amount exists
    if (progressPercentage > 0) {
      scheduledFill.classList.add('right-rounded');
    } else {
      scheduledFill.classList.add('full-rounded');
    }
    
    // Setup hover events for overall tooltip
    setupOverallProgressTooltip(scheduledFill);
  }
}

/**
 * Populate category dropdown with budget categories
 */
function populateCategoryDropdown() {
  const categorySelect = document.getElementById('expense-category-select');
  if (!categorySelect || !expenseTrackerState.currentBudgetData.categories) {
    return;
  }

  // Clear existing options (except first one)
  categorySelect.innerHTML = '<option value="">Select category...</option>';
  
  // Add categories sorted by index (0, 1, 2, etc)
  const categories = Object.entries(expenseTrackerState.currentBudgetData.categories)
    .sort(([a], [b]) => parseInt(a) - parseInt(b));
  
  categories.forEach(([categoryId, category]) => {
    const option = document.createElement('option');
    option.value = categoryId;
    option.textContent = category.name || 'Unnamed Category';
    categorySelect.appendChild(option);
  });

  // Add "Unplanned" pseudo-category for expenses that don't fit any category
  const unplannedOption = document.createElement('option');
  unplannedOption.value = 'unplanned';
  unplannedOption.textContent = '+ Unplanned';
  unplannedOption.style.fontStyle = 'italic';
  categorySelect.appendChild(unplannedOption);
}

/**
 * Populate line item dropdown based on selected category
 */
function populateLineItemDropdown(categoryId) {
  const lineItemSelect = document.getElementById('expense-line-item-select');
  if (!lineItemSelect) return;

  // Clear existing options
  lineItemSelect.innerHTML = '<option value="">Select line item (optional)</option>';

  if (!categoryId || !expenseTrackerState.currentBudgetData.categories[categoryId]) {
    lineItemSelect.disabled = true;
    return;
  }

  const category = expenseTrackerState.currentBudgetData.categories[categoryId];
  console.log('Selected category data:', category);
  lineItemSelect.disabled = false;

  // Add existing line items
  if (category.items && Array.isArray(category.items)) {
    console.log('Category items:', category.items);
    
    // Keep items in original order (0, 1, 2, etc)
    category.items.forEach((item, index) => {
      console.log('Processing item:', item);
      const option = document.createElement('option');
      option.value = index.toString(); // Use array index as ID
      option.textContent = item.label || 'Unnamed Item';
      
      // Add tax deductible indicator
      if (item.isTaxDeductible) {
        option.textContent += ' 🧾';
      }
      
      lineItemSelect.appendChild(option);
    });
  } else {
    console.log('No items found in category or items is not an array');
  }

}

/**
 * Get expenses for the current month
 */
async function getCurrentMonthExpenses() {
  try {
    const currentMonth = new Date().toISOString().slice(0, 7);
    const expensesQuery = query(
      getUserExpenseCollection(),
      where('month', '==', currentMonth),
      orderBy('date', 'desc')
    );
    
    const querySnapshot = await getDocs(expensesQuery);
    return querySnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('Failed to get current month expenses:', error);
    return [];
  }
}

/**
 * Load upcoming recurring expenses
 */
async function loadUpcomingRecurringExpenses() {
  try {
    const currentMonth = new Date().toISOString().slice(0, 7);
    
    // Get current month recurring expenses (this will generate them internally)
    console.log('🔍 [Duplicate Debug] Getting recurring expenses for current month...');
    const now = new Date();
    const recurringExpenses = await getCurrentMonthRecurringExpenses();
    console.log('🔍 [Duplicate Debug] Retrieved recurring expenses:', {
      totalCount: recurringExpenses.length,
      housingExpenses: recurringExpenses.filter(e => e.categoryName === '🏠 Housing').map(e => ({
        name: e.name || e.label,
        amount: e.amount,
        status: e.status,
        dueDate: e.dueDate,
        id: e.id || 'no-id'
      }))
    });
    
    // Filter to next 7 days
    const nextWeek = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
    
    // Get current month's actual expenses to check for already-logged recurring expenses
    const currentMonthExpenses = await getCurrentMonthExpenses();
    
    console.log('[Upcoming Debug] Current month expenses with recurring info:', 
      currentMonthExpenses.filter(e => e.isRecurringPayment).map(e => ({
        lineItemName: e.lineItemName,
        recurringId: e.recurringId,
        isRecurringPayment: e.isRecurringPayment,
        amount: e.amount,
        date: e.date ? e.date.toDate() : 'no date'
      }))
    );
    
    console.log('[Upcoming Debug] All generated recurring expenses:', 
      recurringExpenses.map(e => ({
        name: e.name || e.label,
        id: e.id,
        originalId: e.originalId,
        status: e.status,
        dueDate: e.dueDate
      }))
    );
    
    const upcomingExpenses = recurringExpenses.filter(expense => {
      const expenseDate = new Date(expense.dueDate || expense.date);
      if (!(expenseDate >= now && expenseDate <= nextWeek && expense.status === 'pending')) {
        return false;
      }
      
      // Check if this recurring expense has already been logged this month
      // First check the database for logged expenses  
      const alreadyLoggedInDB = currentMonthExpenses.some(actualExpense => 
        actualExpense.isRecurringPayment && 
        actualExpense.recurringId === (expense.originalId || expense.id)
      );
      
      const alreadyLogged = alreadyLoggedInDB;
      
      console.log(`[Upcoming Debug] Expense: ${expense.name || expense.label}, ID: ${expense.id}, DB logged: ${alreadyLoggedInDB}, Already logged: ${alreadyLogged}`);
      console.log(`[Filter Debug] ${expense.name || expense.label}: status="${expense.status}", dateCheck=${expenseDate >= now && expenseDate <= nextWeek}, shouldInclude=${!alreadyLogged}`);
      
      return !alreadyLogged;
    });

    expenseTrackerState.upcomingRecurring = upcomingExpenses;
    
    console.log('Upcoming recurring expenses (next 7 days):', upcomingExpenses);
    
    // Update UI
    updateUpcomingRecurringDisplay(upcomingExpenses);
    
  } catch (error) {
    console.error('Failed to load upcoming recurring expenses:', error);
  }
}

/**
 * Update upcoming recurring expenses display
 */
function updateUpcomingRecurringDisplay(upcomingExpenses) {
  const notice = document.getElementById('upcoming-recurring-notice');
  const noticeText = document.getElementById('upcoming-recurring-text');
  const tooltipContent = document.getElementById('upcoming-recurring-tooltip-content');

  if (upcomingExpenses.length > 0) {
    if (notice) notice.style.display = 'flex';
    if (noticeText) {
      noticeText.textContent = `${upcomingExpenses.length} entr${upcomingExpenses.length > 1 ? 'ies' : 'y'} coming up this week`;
    }

    // Populate tooltip with expense details
    if (tooltipContent) {
      tooltipContent.innerHTML = upcomingExpenses.map(expense => {
        const expenseName = expense.name || expense.label || 'Recurring Expense';
        const expenseAmount = formatCurrency(expense.amount || 0);
        const dueDate = new Date(expense.dueDate || expense.date);
        const formattedDate = dueDate.toLocaleDateString('en-US', {
          weekday: 'short',
          month: 'short',
          day: 'numeric'
        });

        return `
          <div class="tooltip-item">
            <span class="tooltip-item-name">${expenseName}</span>
            <span class="tooltip-item-amount">${expenseAmount}</span>
            <span class="tooltip-item-date">${formattedDate}</span>
          </div>
        `;
      }).join('');
    }
  } else {
    if (notice) notice.style.display = 'none';
  }
}

/**
 * Setup event listeners for expense tracker
 */
function setupEventListeners() {
  // Category selection
  const categorySelect = document.getElementById('expense-category-select');
  if (categorySelect) {
    categorySelect.addEventListener('change', handleCategoryChange);
  }

  // Line item selection
  const lineItemSelect = document.getElementById('expense-line-item-select');
  if (lineItemSelect) {
    lineItemSelect.addEventListener('change', handleLineItemChange);
  }

  // Amount input with real-time feedback
  const amountInput = document.getElementById('expense-amount-input');
  if (amountInput) {
    amountInput.addEventListener('input', handleAmountInput);
    amountInput.addEventListener('focus', () => {
      amountInput.select();
      const bar = document.getElementById('amount-operator-bar');
      if (bar) bar.classList.add('visible');
    });
    amountInput.addEventListener('blur', () => {
      handleAmountChange();
      // Delay hide so operator button taps register first
      setTimeout(() => {
        const bar = document.getElementById('amount-operator-bar');
        if (bar) bar.classList.remove('visible');
      }, 150);
    });

    // Handle Enter key to evaluate expressions
    amountInput.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleAmountChange();
        amountInput.blur();
      }
    });
  }

  // Quick amount buttons
  const quickAmountBtns = document.querySelectorAll('.quick-amount-btn');
  quickAmountBtns.forEach(btn => {
    btn.addEventListener('click', () => {
      const amount = btn.dataset.amount;
      if (amountInput) {
        amountInput.value = amount;
        handleAmountChange();
      }
    });
  });

  // Tax deductible checkbox
  const taxCheckbox = document.getElementById('expense-tax-deductible');
  if (taxCheckbox) {
    taxCheckbox.addEventListener('change', handleTaxDeductibleChange);
  }

  // Date input - set default to today
  const dateInput = document.getElementById('expense-date-input');
  const calendarBtn = document.getElementById('expense-date-btn');
  const datePickerPopup = document.getElementById('date-picker-popup');
  
  if (dateInput) {
    const today = formatDateToLocal(new Date()); // Format: YYYY-MM-DD using local time
    dateInput.value = today;
    dateInput.max = today; // Prevent future dates
  }
  
  // Calendar button click handler for custom date picker
  if (calendarBtn && datePickerPopup) {
    setupDatePicker();
  }

  // Submit button
  const submitBtn = document.getElementById('submit-expense-btn');
  if (submitBtn) {
    submitBtn.addEventListener('click', handleSubmitExpense);
  }

  // Form submission with Enter key
  const form = document.querySelector('.expense-form');
  if (form) {
    form.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSubmitExpense();
      }
    });
  }

  // Expand details button
  const expandDetailsBtn = document.getElementById('expand-details-btn');
  console.log('🔘 [Button Debug] Found expand-details-btn:', !!expandDetailsBtn);
  if (expandDetailsBtn) {
    expandDetailsBtn.addEventListener('click', (e) => {
      console.log('🔘 [Button Debug] Filter History button clicked!');
      toggleCategoryDetails();
    });
  }

  // Upcoming recurring expenses tooltip
  const recurringNotice = document.getElementById('upcoming-recurring-notice');
  const recurringTooltip = document.getElementById('upcoming-recurring-tooltip');

  if (recurringNotice && recurringTooltip) {
    // Desktop: show on hover
    recurringNotice.addEventListener('mouseenter', () => {
      recurringTooltip.classList.add('show');
    });

    recurringNotice.addEventListener('mouseleave', () => {
      recurringTooltip.classList.remove('show');
    });

    // Mobile: toggle on click/tap
    recurringNotice.addEventListener('click', (e) => {
      e.stopPropagation();
      recurringTooltip.classList.toggle('show');
    });

    // Close tooltip when clicking outside
    document.addEventListener('click', (e) => {
      if (!recurringNotice.contains(e.target) && !recurringTooltip.contains(e.target)) {
        recurringTooltip.classList.remove('show');
      }
    });
  }
}

/**
 * Toggle category details expansion - now filters expense history instead
 */
function toggleCategoryDetails() {
  console.log('🔘 [Toggle Debug] toggleCategoryDetails called');
  
  const expandBtn = document.getElementById('expand-details-btn');
  const expandText = expandBtn?.querySelector('.expand-text');
  const expandIcon = expandBtn?.querySelector('.expand-icon');
  
  console.log('🔘 [Toggle Debug] Elements found:', {
    expandBtn: !!expandBtn,
    expandText: !!expandText,
    expandIcon: !!expandIcon,
    selectedCategory: expenseTrackerState.selectedCategory
  });
  
  if (!expandBtn || expenseTrackerState.selectedCategory === null || expenseTrackerState.selectedCategory === undefined) {
    console.log('🔘 [Toggle Debug] Early return - missing elements or no selected category');
    return;
  }
  
  // Check if currently filtering
  const headerElement = document.querySelector('.expense-tracker-history .expense-history-header h3');
  const isCurrentlyFiltering = headerElement && headerElement.innerHTML.includes(' - ');
  
  if (isCurrentlyFiltering) {
    // Clear filter
    clearExpenseTrackerHistoryFilter();
    expandBtn.classList.remove('expanded');
    if (expandText) expandText.textContent = 'Filter History';
    if (expandIcon) expandIcon.textContent = '🔍';
  } else {
    // Apply filter for this category using categoryName
    const categoryId = expenseTrackerState.selectedCategory;
    const category = expenseTrackerState.currentBudgetData.categories[categoryId];
    const categoryName = category?.name || '';
    
    console.log('🔍 [Filter Debug] Filtering history:', {
      categoryId,
      categoryName,
      selectedCategory: expenseTrackerState.selectedCategory
    });
    
    filterExpenseTrackerHistoryByCategory(categoryName);
    expandBtn.classList.add('expanded');
    if (expandText) expandText.textContent = 'Clear Filter';
    if (expandIcon) expandIcon.textContent = '✕';
  }
}

/**
 * Load and display category expense details
 */
async function loadCategoryExpenseDetails() {
  const expensesList = document.getElementById('category-expenses-list');
  const totalElement = document.getElementById('category-total-amount');
  
  if (expenseTrackerState.selectedCategory === null || expenseTrackerState.selectedCategory === undefined || !expensesList) return;
  
  try {
    const currentMonth = new Date().toISOString().slice(0, 7);
    const budgetId = getCurrentBudgetId();
    
    // Show loading state
    expensesList.innerHTML = `
      <div style="padding: 20px; text-align: center; color: #666;">
        <div class="loading-spinner" style="margin: 0 auto 10px;"></div>
        <p>Loading expenses...</p>
      </div>
    `;
    
    // Get expenses for this category
    const expenses = await getCategoryExpenses(budgetId, expenseTrackerState.selectedCategory, currentMonth);
    
    console.log('Retrieved category expenses:', expenses);
    
    if (expenses.length === 0) {
      expensesList.innerHTML = `
        <div style="padding: 20px; text-align: center; color: #666;">
          <p>No expenses recorded for this category yet.</p>
          <p>Add some expenses using the form above!</p>
        </div>
      `;
      if (totalElement) {
        totalElement.textContent = '$0.00';
      }
      return;
    }
    
    // Calculate total
    const total = expenses.reduce((sum, expense) => sum + expense.amount, 0);
    
    // Update total display
    if (totalElement) {
      totalElement.textContent = `$${total.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }
    
    // Build expenses list HTML
    const expensesHtml = expenses.map(expense => {
      const date = expense.date.toDate();
      const dateStr = date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric',
        year: date.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined
      });
      const timeStr = date.toLocaleTimeString('en-US', { 
        hour: 'numeric', 
        minute: '2-digit',
        hour12: true 
      });
      
      return `
        <div class="expense-item" data-expense-id="${expense.id}" style="display: flex; justify-content: space-between; align-items: flex-start; padding: 12px; border-bottom: 1px solid #ddd; gap: 12px; background: transparent; background-color: transparent; transform: none !important; transition: none !important;">
          <div class="expense-details" style="flex: 1; min-width: 0;">
            <div class="expense-header" style="display: flex; align-items: center; gap: 8px; margin-bottom: 4px;">
              <strong class="expense-line-item" style="color: var(--text-dark);">${expense.lineItemName}</strong>
              ${expense.isTaxDeductible ? '<span style="color: var(--primary-color); padding: 2px 6px; border-radius: 4px; font-size: 0.75em;">🧾 Tax</span>' : ''}
              ${expense.isRecurringPayment ? '<span style="color: var(--budgley-orange); padding: 2px 6px; border-radius: 4px; font-size: 0.75em;">🔄 Recurring</span>' : ''}
            </div>
            ${expense.description ? `<div class="expense-description" style="color: var(--text-light); font-size: 1em; margin-bottom: 4px;">${expense.description}</div>` : ''}
            <div class="expense-meta" style="color: var(--text-light); font-size: 0.95em;">
              ${dateStr} at ${timeStr}
            </div>
          </div>
          <div style="display: flex; align-items: center; gap: 8px;">
            <div class="expense-amount" style="font-weight: 600; color: var(--text-dark); white-space: nowrap;">
              $${expense.amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </div>
            <button class="expense-delete-btn" onclick="deleteExpense('${expense.id}')" style="background: #f8e6e6; color: #dc3545; border: 1px solid #f5c6cb; border-radius: 4px; width: 28px; height: 28px; cursor: pointer; font-size: 14px; display: flex; align-items: center; justify-content: center; transition: all 0.2s ease;" title="Delete expense">
              ×
            </button>
          </div>
        </div>
      `;
    }).join('');
    
    expensesList.innerHTML = expensesHtml;
    
  } catch (error) {
    console.error('Error loading category expense details:', error);
    if (expensesList) {
      expensesList.innerHTML = `
        <div style="padding: 20px; text-align: center; color: #e74c3c;">
          <p>Failed to load expense details</p>
          <p style="font-size: 0.9em; color: #666;">${error.message}</p>
        </div>
      `;
    }
  }
}

/**
 * Get expenses for a specific category and month
 */
async function getCategoryExpenses(budgetId, categoryId, month) {
  try {
    const all = await getCachedMonthExpenses(budgetId, month);
    const expenses = all
      .filter(exp => exp.categoryId === categoryId)
      .slice() // don't mutate the cached array
      .sort((a, b) => {
        const dateA = a.date?.toDate?.() || new Date(a.date);
        const dateB = b.date?.toDate?.() || new Date(b.date);
        return dateB.getTime() - dateA.getTime();
      });
    return expenses;
  } catch (error) {
    console.error('Error getting category expenses:', error);
    throw error;
  }
}

/**
 * Handle category selection change
 */
function handleCategoryChange() {
  const categorySelect = document.getElementById('expense-category-select');
  const selectedCategoryId = categorySelect?.value;
  const lineItemGroup = document.querySelector('.line-item-group');

  console.log('Category changed:', { selectedCategoryId });

  const isUnplannedCategory = selectedCategoryId === 'unplanned';

  // Store category as 'unplanned' string for the pseudo-category, numeric id for real categories
  expenseTrackerState.selectedCategory = isUnplannedCategory
    ? 'unplanned'
    : (selectedCategoryId ? parseInt(selectedCategoryId, 10) : null);
  expenseTrackerState.selectedLineItem = null;

  if (isUnplannedCategory) {
    // Hide line item — unplanned expenses have no line item
    if (lineItemGroup) lineItemGroup.style.display = 'none';
    hideCategoryProgress();
  } else {
    // Show line item dropdown and populate it
    if (lineItemGroup) lineItemGroup.style.display = '';
    populateLineItemDropdown(selectedCategoryId);

    const numericCategoryId = expenseTrackerState.selectedCategory;
    if (numericCategoryId !== null) {
      console.log('Updating category progress for:', numericCategoryId);
      updateCategoryProgress(numericCategoryId);
      showCategoryProgress();
    } else {
      hideCategoryProgress();
    }
  }

  // Update affordability indicator
  updateAffordabilityIndicator();
}

/**
 * Handle line item selection change
 */
function handleLineItemChange() {
  const lineItemSelect = document.getElementById('expense-line-item-select');
  const selectedItemId = lineItemSelect?.value;
  
  expenseTrackerState.selectedLineItem = selectedItemId || null;
  
  // Update tax deductible checkbox based on line item default
  updateTaxDeductibleDefault(selectedItemId);
  
  // Show/update budget remaining info
  if (selectedItemId && selectedItemId !== '') {
    showBudgetRemainingInfo();
  } else {
    hideBudgetRemainingInfo();
  }
  
  // Update affordability indicator
  updateAffordabilityIndicator();
}

/**
 * Handle amount input change with debouncing
 */
let affordabilityTimeout;
/**
 * Handle real-time input for visual feedback (no evaluation)
 */
function handleAmountInput() {
  const amountInput = document.getElementById('expense-amount-input');
  if (!amountInput) return;
  
  const inputValue = amountInput.value.trim();
  
  // Remove all math-related classes first
  amountInput.classList.remove('math-expression', 'math-valid');
  
  if (!inputValue) return;
  
  // Check if it looks like a math expression (contains operators)
  const hasMathOperators = /[+\-*/()]/.test(inputValue);
  
  if (hasMathOperators) {
    amountInput.classList.add('math-expression');
    
    // Check if the expression is valid
    const mathResult = evaluateMathExpression(inputValue);
    if (mathResult !== null) {
      amountInput.classList.add('math-valid');
    }
  }
  
  // Only update affordability indicator, don't evaluate expression
  handleAmountInputChange();
}

/**
 * Handle amount changes (for blur/enter - evaluates expressions)
 */
function handleAmountChange() {
  const amountInput = document.getElementById('expense-amount-input');
  if (amountInput) {
    let inputValue = amountInput.value.trim();
    
    // Try to evaluate as math expression first
    const mathResult = evaluateMathExpression(inputValue);
    if (mathResult !== null) {
      // Update the input field to show the calculated result
      amountInput.value = mathResult.toString();
      // Remove math feedback classes when calculated
      amountInput.classList.remove('math-expression', 'math-valid');
    }
  }
  
  // Update affordability indicator
  updateAffordabilityIndicator();
}

/**
 * Handle amount input changes (for real-time updates without evaluation)
 */
function handleAmountInputChange() {
  // Debounce the affordability check to avoid too many API calls
  clearTimeout(affordabilityTimeout);
  affordabilityTimeout = setTimeout(() => {
    updateAffordabilityIndicator();
  }, 300); // 300ms delay
}

/**
 * Handle tax deductible checkbox change
 */
function handleTaxDeductibleChange() {
  // This is handled automatically by the checkbox state
  // Could add visual feedback here if needed
}

/**
 * Update tax deductible checkbox based on line item default
 */
function updateTaxDeductibleDefault(lineItemId) {
  const taxCheckbox = document.getElementById('expense-tax-deductible');
  if (!taxCheckbox || expenseTrackerState.selectedCategory === null || expenseTrackerState.selectedCategory === undefined || !lineItemId) return;
  
  if (lineItemId === 'unplanned') {
    taxCheckbox.checked = false;
    return;
  }
  
  const category = expenseTrackerState.currentBudgetData.categories[expenseTrackerState.selectedCategory];
  if (category?.items && Array.isArray(category.items)) {
    const itemIndex = parseInt(lineItemId);
    const item = category.items[itemIndex];
    if (item) {
      taxCheckbox.checked = item.isTaxDeductible || false;
    }
  }
}

/**
 * Show and update budget remaining info
 */
async function showBudgetRemainingInfo() {
  const remainingInfo = document.getElementById('budget-remaining-info');
  const weekRemainingEl = document.getElementById('week-remaining');
  const monthRemainingEl = document.getElementById('month-remaining');
  
  if (!remainingInfo || !weekRemainingEl || !monthRemainingEl) return;
  
  try {
    // Check if selected item has weekly or bi-weekly frequency
    let isWeeklyItem = false;
    let isBiWeeklyItem = false;
    if (expenseTrackerState.selectedCategory && expenseTrackerState.selectedLineItem && expenseTrackerState.selectedLineItem !== 'unplanned') {
      const category = expenseTrackerState.currentBudgetData.categories[expenseTrackerState.selectedCategory];
      if (category?.items && Array.isArray(category.items)) {
        const itemIndex = parseInt(expenseTrackerState.selectedLineItem);
        const item = category.items[itemIndex];
        if (item && item.frequency === 'weekly') {
          isWeeklyItem = true;
        } else if (item && item.frequency === 'bi-weekly') {
          isBiWeeklyItem = true;
        }
      }
    }
    
    // Calculate remaining budgets
    const { weekRemaining, monthRemaining, biWeeklyRemaining } = await calculateRemainingBudgets();
    
    // Show/hide week remaining info based on frequency
    const weekRemainingParent = weekRemainingEl.parentElement;
    const weekRemainingLabel = weekRemainingParent?.querySelector('.remaining-label');
    
    if (weekRemainingParent) {
      if (isWeeklyItem) {
        weekRemainingParent.style.display = '';
        if (weekRemainingLabel) weekRemainingLabel.textContent = 'Week remaining:';
        
        // Update week remaining
        weekRemainingEl.textContent = `$${weekRemaining.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
        weekRemainingEl.className = 'remaining-value';
        if (weekRemaining < 0) {
          weekRemainingEl.classList.add('danger');
        } else if (weekRemaining < 50) { // Less than $50 for the week
          weekRemainingEl.classList.add('warning');
        }
      } else if (isBiWeeklyItem) {
        weekRemainingParent.style.display = '';
        if (weekRemainingLabel) weekRemainingLabel.textContent = '2 weeks remaining:';
        
        // Update bi-weekly remaining
        weekRemainingEl.textContent = `$${biWeeklyRemaining.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
        weekRemainingEl.className = 'remaining-value';
        if (biWeeklyRemaining < 0) {
          weekRemainingEl.classList.add('danger');
        } else if (biWeeklyRemaining < 100) { // Less than $100 for 2 weeks
          weekRemainingEl.classList.add('warning');
        }
      } else {
        weekRemainingParent.style.display = 'none';
      }
    }
    
    // Update month remaining (always shown)
    monthRemainingEl.textContent = `$${monthRemaining.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    monthRemainingEl.className = 'remaining-value';
    if (monthRemaining < 0) {
      monthRemainingEl.classList.add('danger');
    } else if (monthRemaining < 200) { // Less than $200 for the month
      monthRemainingEl.classList.add('warning');
    }
    
    // Show the info
    remainingInfo.style.display = 'flex';
    
  } catch (error) {
    console.error('Error calculating remaining budgets:', error);
    hideBudgetRemainingInfo();
  }
}

/**
 * Hide budget remaining info
 */
function hideBudgetRemainingInfo() {
  const remainingInfo = document.getElementById('budget-remaining-info');
  if (remainingInfo) {
    remainingInfo.style.display = 'none';
  }
}

/**
 * Get start of current bi-weekly period
 */
function getBiWeekStartOfWeek(date) {
  const startOfWeek = getStartOfWeek(date);
  const weeksSinceEpoch = Math.floor((startOfWeek.getTime() - new Date(1970, 0, 4).getTime()) / (7 * 24 * 60 * 60 * 1000));
  const biWeeklyPeriod = Math.floor(weeksSinceEpoch / 2);
  const biWeekStartWeek = biWeeklyPeriod * 2;
  const daysDiff = (weeksSinceEpoch - biWeekStartWeek) * 7;
  const biWeekStart = new Date(startOfWeek.getTime() - daysDiff * 24 * 60 * 60 * 1000);
  return biWeekStart;
}

/**
 * Get end of current bi-weekly period
 */
function getBiWeekEndOfWeek(date) {
  const biWeekStart = getBiWeekStartOfWeek(date);
  const biWeekEnd = new Date(biWeekStart.getTime() + 13 * 24 * 60 * 60 * 1000); // 14 days - 1 ms
  return biWeekEnd;
}

/**
 * Calculate weekly and monthly remaining budgets for selected line item
 */
async function calculateRemainingBudgets() {
  const now = new Date();
  const currentMonth = now.toISOString().slice(0, 7);
  const budgetId = getCurrentBudgetId();
  
  if (expenseTrackerState.selectedCategory === null || expenseTrackerState.selectedCategory === undefined || !expenseTrackerState.selectedLineItem) {
    return { weekRemaining: 0, monthRemaining: 0 };
  }
  
  // Get selected line item details
  const category = expenseTrackerState.currentBudgetData.categories[expenseTrackerState.selectedCategory];
  if (!category?.items || !Array.isArray(category.items)) {
    return { weekRemaining: 0, monthRemaining: 0 };
  }
  
  let lineItemMonthlyBudget = 0;
  let lineItemName = '';
  
  if (expenseTrackerState.selectedLineItem === 'unplanned') {
    // Unplanned items have no budget allocated
    return { weekRemaining: 0, monthRemaining: 0 };
  } else {
    const itemIndex = parseInt(expenseTrackerState.selectedLineItem);
    const item = category.items[itemIndex];
    if (!item) {
      return { weekRemaining: 0, monthRemaining: 0 };
    }
    
    lineItemMonthlyBudget = convertToMonthlyAmount(item.amount || 0, item.frequency || 'monthly');
    lineItemName = item.label || 'Unnamed Item';
  }
  
  // Get actual expenses for this specific line item this month
  const monthlySpentOnItem = await getLineItemExpenseTotal(
    budgetId, 
    expenseTrackerState.selectedCategory,
    expenseTrackerState.selectedLineItem,
    currentMonth
  );
  
  // Get scheduled recurring for this line item this month
  const monthlyScheduledOnItem = await getLineItemScheduledRecurring(
    expenseTrackerState.selectedCategory,
    expenseTrackerState.selectedLineItem,
    currentMonth
  );
  
  const monthRemaining = lineItemMonthlyBudget - monthlySpentOnItem - monthlyScheduledOnItem;
  
  // Calculate weekly budget and spending for this line item
  const weeklyBudget = lineItemMonthlyBudget / 4.33; // Average weeks per month
  
  // Get expenses for current week for this line item
  const weekStart = getStartOfWeek(now);
  const weekEnd = getEndOfWeek(now);
  const weeklySpentOnItem = await getLineItemWeeklyExpenseTotal(
    budgetId,
    expenseTrackerState.selectedCategory,
    expenseTrackerState.selectedLineItem,
    weekStart,
    weekEnd
  );
  
  // Get scheduled recurring for current week for this line item
  const weeklyScheduledOnItem = await getLineItemWeeklyScheduledRecurring(
    expenseTrackerState.selectedCategory,
    expenseTrackerState.selectedLineItem,
    weekStart,
    weekEnd
  );
  
  const weekRemaining = weeklyBudget - weeklySpentOnItem - weeklyScheduledOnItem;
  
  // Calculate bi-weekly budget for this line item
  // For bi-weekly items, use the original expense line amount directly
  let biWeeklyBudget = 0;
  if (expenseTrackerState.selectedCategory && expenseTrackerState.selectedLineItem && expenseTrackerState.selectedLineItem !== 'unplanned') {
    const category = expenseTrackerState.currentBudgetData.categories[expenseTrackerState.selectedCategory];
    if (category?.items && Array.isArray(category.items)) {
      const itemIndex = parseInt(expenseTrackerState.selectedLineItem);
      const item = category.items[itemIndex];
      if (item && item.frequency === 'bi-weekly') {
        biWeeklyBudget = item.amount || 0; // Use original amount for bi-weekly items
      } else {
        biWeeklyBudget = lineItemMonthlyBudget / 2.17; // Fallback for other frequencies
      }
    }
  }
  
  // Get expenses for current bi-weekly period for this line item
  const biWeekStart = getBiWeekStartOfWeek(now);
  const biWeekEnd = getBiWeekEndOfWeek(now);
  const biWeeklySpentOnItem = await getLineItemWeeklyExpenseTotal(
    budgetId,
    expenseTrackerState.selectedCategory,
    expenseTrackerState.selectedLineItem,
    biWeekStart,
    biWeekEnd
  );
  
  // Get scheduled recurring for current bi-weekly period for this line item  
  const biWeeklyScheduledOnItem = await getLineItemWeeklyScheduledRecurring(
    expenseTrackerState.selectedCategory,
    expenseTrackerState.selectedLineItem,
    biWeekStart,
    biWeekEnd
  );
  
  const biWeeklyRemaining = biWeeklyBudget - biWeeklySpentOnItem - biWeeklyScheduledOnItem;
  
  return {
    weekRemaining,
    monthRemaining,
    biWeeklyRemaining
  };
}

/**
 * Get start of current week (Sunday)
 */
function getStartOfWeek(date) {
  const start = new Date(date);
  const day = start.getDay();
  const diff = start.getDate() - day;
  start.setDate(diff);
  start.setHours(0, 0, 0, 0);
  return start;
}

/**
 * Get end of current week (Saturday)
 */
function getEndOfWeek(date) {
  const end = new Date(date);
  const day = end.getDay();
  const diff = end.getDate() - day + 6;
  end.setDate(diff);
  end.setHours(23, 59, 59, 999);
  return end;
}

/**
 * Get total expenses for a specific week
 */
async function getWeeklyExpenseTotal(budgetId, weekStart, weekEnd) {
  try {
    const expensesQuery = query(
      getUserExpenseCollection(),
      where('budgetId', '==', budgetId),
      where('date', '>=', Timestamp.fromDate(weekStart)),
      where('date', '<=', Timestamp.fromDate(weekEnd))
    );

    const snapshot = await getDocs(expensesQuery);
    let total = 0;

    snapshot.forEach(doc => {
      const expense = doc.data();
      const amount = expense.amount || 0;
      // Returns reduce the total spent (subtract instead of add)
      total += expense.isReturn ? -amount : amount;
    });

    return total;
  } catch (error) {
    console.error('Failed to get weekly expense total:', error);
    return 0;
  }
}

/**
 * Get scheduled recurring expenses for current week
 */
async function getWeeklyScheduledRecurring(weekStart, weekEnd) {
  try {
    const recurringExpenses = await getCurrentMonthRecurringExpenses();
    return recurringExpenses
      .filter(expense => {
        if (expense.status !== 'scheduled' && expense.status !== 'pending') return false;
        const expenseDate = new Date(expense.date);
        return expenseDate >= weekStart && expenseDate <= weekEnd;
      })
      .reduce((total, expense) => total + (expense.amount || 0), 0);
  } catch (error) {
    console.error('Failed to get weekly scheduled recurring total:', error);
    return 0;
  }
}

/**
 * Get total expenses for a specific line item this month
 */
async function getLineItemExpenseTotal(budgetId, categoryId, lineItemId, month) {
  try {
    const expenses = await getCachedMonthExpenses(budgetId, month);
    let total = 0;
    for (const expense of expenses) {
      if (expense.categoryId !== categoryId || expense.lineItemId !== lineItemId) continue;
      const amount = expense.amount || 0;
      total += expense.isReturn ? -amount : amount;
    }
    return total;
  } catch (error) {
    console.error('Failed to get line item expense total:', error);
    return 0;
  }
}

/**
 * Get scheduled recurring expenses for a specific line item this month
 */
async function getLineItemScheduledRecurring(categoryId, lineItemId, month) {
  try {
    const recurringExpenses = await getCurrentMonthRecurringExpenses();
    
    // Get the category name and line item name from the budget structure
    const category = expenseTrackerState.currentBudgetData.categories[categoryId];
    const categoryName = category?.name || '';
    
    let lineItemName = '';
    if (lineItemId !== 'unplanned' && category?.items && Array.isArray(category.items)) {
      const itemIndex = parseInt(lineItemId);
      const item = category.items[itemIndex];
      lineItemName = item?.label || '';
    }
    
    return recurringExpenses
      .filter(expense => 
        (expense.status === 'scheduled' || expense.status === 'pending') &&
        expense.categoryName === categoryName &&
        (expense.label === lineItemName || expense.name === lineItemName)
      )
      .reduce((total, expense) => total + (expense.amount || 0), 0);
  } catch (error) {
    console.error('Failed to get line item scheduled recurring total:', error);
    return 0;
  }
}

/**
 * Get total expenses for a specific line item this week
 */
async function getLineItemWeeklyExpenseTotal(budgetId, categoryId, lineItemId, weekStart, weekEnd) {
  try {
    const db = getFirestore();
    const expensesQuery = query(
      getUserExpenseCollection(),
      where('budgetId', '==', budgetId),
      where('categoryId', '==', categoryId),
      where('lineItemId', '==', lineItemId),
      where('date', '>=', Timestamp.fromDate(weekStart)),
      where('date', '<=', Timestamp.fromDate(weekEnd))
    );

    const snapshot = await getDocs(expensesQuery);
    let total = 0;

    snapshot.forEach(doc => {
      const expense = doc.data();
      const amount = expense.amount || 0;
      // Returns reduce the total spent (subtract instead of add)
      total += expense.isReturn ? -amount : amount;
    });

    return total;
  } catch (error) {
    console.error('Failed to get line item weekly expense total:', error);
    return 0;
  }
}

/**
 * Get scheduled recurring expenses for a specific line item this week
 */
async function getLineItemWeeklyScheduledRecurring(categoryId, lineItemId, weekStart, weekEnd) {
  try {
    const recurringExpenses = await getCurrentMonthRecurringExpenses();
    
    // Get the category name and line item name from the budget structure
    const category = expenseTrackerState.currentBudgetData.categories[categoryId];
    const categoryName = category?.name || '';
    
    let lineItemName = '';
    if (lineItemId !== 'unplanned' && category?.items && Array.isArray(category.items)) {
      const itemIndex = parseInt(lineItemId);
      const item = category.items[itemIndex];
      lineItemName = item?.label || '';
    }
    
    return recurringExpenses
      .filter(expense => {
        if (expense.status !== 'scheduled' && expense.status !== 'pending') return false;
        if (expense.categoryName !== categoryName) return false;
        if (expense.label !== lineItemName && expense.name !== lineItemName) return false;
        
        const expenseDate = new Date(expense.date);
        return expenseDate >= weekStart && expenseDate <= weekEnd;
      })
      .reduce((total, expense) => total + (expense.amount || 0), 0);
  } catch (error) {
    console.error('Failed to get line item weekly scheduled recurring total:', error);
    return 0;
  }
}

/**
 * Update affordability indicator based on current input for selected line item
 */
async function updateAffordabilityIndicator() {
  const amountInput = document.getElementById('expense-amount-input');
  const indicator = document.getElementById('affordability-indicator');
  const indicatorIcon = indicator?.querySelector('.indicator-icon');
  const indicatorText = indicator?.querySelector('.indicator-text');
  
  if (!indicator || !amountInput) {
    return;
  }
  
  // Check if we have a category selected
  if (expenseTrackerState.selectedCategory === null || expenseTrackerState.selectedCategory === undefined) {
    indicator.className = 'affordability-indicator good';
    if (indicatorIcon) indicatorIcon.textContent = '💚';
    if (indicatorText) indicatorText.textContent = 'Select category';
    return;
  }

  const amount = parseAmountInput(amountInput.value);
  if (amount <= 0) {
    indicator.className = 'affordability-indicator good';
    if (indicatorIcon) indicatorIcon.textContent = '💚';
    if (indicatorText) indicatorText.textContent = 'Enter amount';
    return;
  }

  try {
    // Show loading state
    if (indicatorText) indicatorText.textContent = 'Checking...';

    // Tier 3: Unplanned pseudo-category — no budget to check against
    if (expenseTrackerState.selectedCategory === 'unplanned') {
      indicator.className = 'affordability-indicator warning';
      if (indicatorIcon) indicatorIcon.textContent = '⚠️';
      if (indicatorText) indicatorText.textContent = 'Unplanned expense';
      return;
    }

    // Tier 2: Category selected but no line item — uncategorized under a known category
    if (!expenseTrackerState.selectedLineItem) {
      indicator.className = 'affordability-indicator warning';
      if (indicatorIcon) indicatorIcon.textContent = '⚠️';
      if (indicatorText) indicatorText.textContent = 'Uncategorized';
      return;
    }

    // Legacy guard: old 'unplanned' line item value (pre-migration data)
    if (expenseTrackerState.selectedLineItem === 'unplanned') {
      indicator.className = 'affordability-indicator warning';
      if (indicatorIcon) indicatorIcon.textContent = '⚠️';
      if (indicatorText) indicatorText.textContent = 'Unplanned expense';
      return;
    }
    
    // Get line item budget and remaining amounts
    const { monthRemaining } = await calculateRemainingBudgets();
    
    // Get line item monthly budget for percentage calculations
    const category = expenseTrackerState.currentBudgetData?.categories?.[expenseTrackerState.selectedCategory];
    if (!category || !category.items) {
      console.warn('Category or items not found for affordability indicator');
      return;
    }
    
    const itemIndex = parseInt(expenseTrackerState.selectedLineItem);
    const item = category.items[itemIndex];
    if (!item) {
      console.warn('Line item not found at index:', itemIndex);
      return;
    }
    
    const lineItemMonthlyBudget = convertToMonthlyAmount(item.amount || 0, item.frequency || 'monthly');
    
    // Determine indicator state based on line item budget
    let state = 'good';
    let icon = '💚';
    let text = 'You\'re on track';
    
    if (monthRemaining < amount) {
      state = 'danger';
      icon = '❌';
      text = 'Over line item budget';
    } else if (monthRemaining - amount < lineItemMonthlyBudget * 0.1) {
      state = 'warning';
      icon = '⚠️';
      text = 'Line item running low';
    } else if (monthRemaining - amount < lineItemMonthlyBudget * 0.2) {
      state = 'warning';
      icon = '⚠️';
      text = 'Getting close to limit';
    }
    
    indicator.className = `affordability-indicator ${state}`;
    if (indicatorIcon) indicatorIcon.textContent = icon;
    if (indicatorText) indicatorText.textContent = text;
    
  } catch (error) {
    console.error('Error updating affordability indicator:', error);
    indicator.className = 'affordability-indicator good';
    if (indicatorIcon) indicatorIcon.textContent = '💚';
    if (indicatorText) indicatorText.textContent = 'Error checking budget';
  }
}

/**
 * Handle expense submission
 */
let submissionInProgress = false;
async function handleSubmitExpense() {
  console.log('handleSubmitExpense called, submissionInProgress:', submissionInProgress);
  
  // Prevent double submission
  if (submissionInProgress) {
    console.log('Submission already in progress, ignoring');
    return;
  }
  
  submissionInProgress = true;
  
  const submitBtn = document.getElementById('submit-expense-btn');
  const btnText = submitBtn?.querySelector('.btn-text');
  const btnLoading = submitBtn?.querySelector('.btn-loading');
  
  try {
    // Validate form
    const validation = validateExpenseForm();
    if (!validation.valid) {
      showError(validation.message);
      submissionInProgress = false;
      return;
    }
    
    // Show loading state
    if (submitBtn) submitBtn.disabled = true;
    if (btnText) btnText.style.display = 'none';
    if (btnLoading) btnLoading.style.display = 'flex';
    
    const formData = getExpenseFormData();
    
    // Check for duplicate recurring expenses
    const duplicateCheck = await checkForDuplicateRecurring(formData);
    if (duplicateCheck.hasDuplicate) {
      // Show duplicate warning modal
      const userChoice = await showDuplicateWarningModal(duplicateCheck.matches);
      if (userChoice.action === 'cancel') {
        submissionInProgress = false;
        return;
      }
      
      // Handle user choice
      if (userChoice.action === 'link') {
        formData.recurringExpenseId = userChoice.recurringId;
        formData.isRecurringPayment = true;
      }
    }
    
    // Submit expense
    await submitExpense(formData);

    // Show success feedback
    showSuccess('Expense added successfully!');

    // Mark getting-started step
    if (typeof window.markGettingStartedStep === 'function') {
      window.markGettingStartedStep('logExpense');
    }

    // Clear form and refresh data (remaining info will auto-refresh)
    clearExpenseForm();
    window.dispatchEvent(new CustomEvent('expense-submitted'));
    await updateOverallProgress();
    
    // Refresh expense history to show the new expense
    await loadExpenseTrackerHistory();
    
    if (expenseTrackerState.selectedCategory) {
      updateCategoryProgress(expenseTrackerState.selectedCategory);
      
      // Refresh expense details if they're currently expanded
      const detailsSection = document.getElementById('category-details');
      if (detailsSection && detailsSection.style.display !== 'none') {
        await loadCategoryExpenseDetails();
      }
    }
    
    // Refresh progress data in other modules (like progress view)
    if (window.refreshAllProgressData) {
      await window.refreshAllProgressData();
    }
    
  } catch (error) {
    console.error('Failed to submit expense:', error);
    showError('Failed to add expense. Please try again.');
  } finally {
    // Reset button state
    if (submitBtn) submitBtn.disabled = false;
    if (btnText) btnText.style.display = 'inline';
    if (btnLoading) btnLoading.style.display = 'none';
    
    // Reset submission flag
    submissionInProgress = false;
  }
}

/**
 * Validate expense form data
 */
function validateExpenseForm() {
  const categorySelect = document.getElementById('expense-category-select');
  const lineItemSelect = document.getElementById('expense-line-item-select');
  const amountInput = document.getElementById('expense-amount-input');
  
  if (!categorySelect?.value) {
    return { valid: false, message: 'Please select a category' };
  }

  const amount = parseAmountInput(amountInput?.value || '0');
  if (amount <= 0) {
    return { valid: false, message: 'Please enter a valid amount' };
  }
  
  return { valid: true };
}

/**
 * Get expense form data
 */
function getExpenseFormData() {
  const categorySelect = document.getElementById('expense-category-select');
  const lineItemSelect = document.getElementById('expense-line-item-select');
  const amountInput = document.getElementById('expense-amount-input');
  const descriptionInput = document.getElementById('expense-description-input');
  const taxCheckbox = document.getElementById('expense-tax-deductible');
  const returnCheckbox = document.getElementById('expense-is-return');
  const dateInput = document.getElementById('expense-date-input');

  const user = getCurrentUser();
  const budgetId = getCurrentBudgetId();
  const now = new Date();

  // Use selected date or default to today with current time
  const selectedDateValue = dateInput?.value;
  let expenseDate;

  if (selectedDateValue) {
    const selectedDate = new Date(selectedDateValue);
    const todayString = formatDateForInput(now);

    if (selectedDateValue === todayString) {
      // If selected date is today, use current timestamp
      expenseDate = now;
    } else {
      // If user selected a different date, set it to 12:00 PM on that date
      expenseDate = new Date(selectedDateValue + 'T12:00:00');
    }
  } else {
    // If no date selected, use current timestamp
    expenseDate = now;
  }

  const expenseMonth = expenseDate.toISOString().slice(0, 7);

  const categoryValue = categorySelect?.value;
  const lineItemValue = lineItemSelect?.value;

  // Tier 3: "Unplanned" pseudo-category — no real category or line item
  const isUnplanned = categoryValue === 'unplanned';

  // Tier 2: Real category selected but no line item chosen
  const isUncategorized = !isUnplanned && (!lineItemValue || lineItemValue === '');

  // Resolve category id and name
  let categoryId = null;
  let categoryName = null;
  if (isUnplanned) {
    categoryId = 'unplanned';
    categoryName = 'Unplanned';
  } else if (categoryValue) {
    categoryId = parseInt(categoryValue, 10);
    const cat = expenseTrackerState.currentBudgetData?.categories?.[categoryId];
    if (cat) categoryName = cat.name || null;
  }

  // Resolve line item id and name
  let lineItemId = null;
  let lineItemName = null;
  if (!isUnplanned && !isUncategorized) {
    // Tier 1: normal expense with a specific line item
    lineItemId = lineItemValue;
    const cat = expenseTrackerState.currentBudgetData?.categories?.[categoryId];
    if (cat?.items && Array.isArray(cat.items)) {
      const item = cat.items[parseInt(lineItemValue)];
      if (item) lineItemName = item.label || 'Unnamed Item';
    }
  } else if (isUncategorized) {
    lineItemId = 'uncategorized';
    lineItemName = 'Uncategorized';
  }

  return {
    budgetId,
    categoryId,
    categoryName,
    lineItemId,
    lineItemName,
    amount: parseAmountInput(amountInput?.value || '0'),
    description: descriptionInput?.value?.trim() || null,
    date: Timestamp.fromDate(expenseDate),
    month: expenseMonth,
    isUnplanned,
    isUncategorized,
    isReturn: returnCheckbox?.checked || false,
    isTaxDeductible: taxCheckbox?.checked || false,
    createdAt: Timestamp.fromDate(now),
    updatedAt: Timestamp.fromDate(now)
  };
}

/**
 * Submit expense to Firestore
 */
async function submitExpense(expenseData) {
  const db = getFirestore();
  const expensesCollection = getUserExpenseCollection();

  // Add expense document
  const expenseDoc = await addDoc(expensesCollection, expenseData);

  // Clear expense cache to ensure fresh data on next load
  if (typeof window !== 'undefined' && window.clearExpenseCache) {
    window.clearExpenseCache();
  }

  // Update monthly totals (this would typically be done by a Cloud Function)
  // For now, we'll update it client-side
  await updateMonthlyTotals(expenseData);

  // Update monthly progress (debounced to avoid excessive writes)
  debouncedUpdateMonthlyProgress(expenseData);

  // Track achievement for expense logging
  const user = getCurrentUser();
  if (user) {
    await trackExpenseLogging(user.uid);
  }

  return expenseDoc;
}

/**
 * Debounced monthly progress update
 * Waits 5 seconds after last expense before updating
 */
function debouncedUpdateMonthlyProgress(expenseData) {
  // Clear existing timer
  if (monthlyProgressUpdateTimer) {
    clearTimeout(monthlyProgressUpdateTimer);
  }

  // Set new timer
  monthlyProgressUpdateTimer = setTimeout(async () => {
    try {
      const user = getCurrentUser();
      if (!user) return;

      const month = expenseData.month; // Already in YYYY-MM format
      await updateMonthlyProgress(user.uid, month);
      console.log(`📊 Monthly progress updated for ${month}`);
    } catch (error) {
      console.error('Error updating monthly progress:', error);
    }
  }, 5000); // 5 second debounce
}

/**
 * Clear expense form but keep category selection and progress
 */
function clearExpenseForm() {
  const amountInput = document.getElementById('expense-amount-input');
  const descriptionInput = document.getElementById('expense-description-input');
  const taxCheckbox = document.getElementById('expense-tax-deductible');
  const returnCheckbox = document.getElementById('expense-is-return');

  // Clear amount and description only
  if (amountInput) amountInput.value = '';
  if (descriptionInput) descriptionInput.value = '';

  // Reset checkboxes to default (unchecked)
  if (taxCheckbox) taxCheckbox.checked = false;
  if (returnCheckbox) returnCheckbox.checked = false;

  // Keep line item group hidden if still on Unplanned category
  const lineItemGroup = document.querySelector('.line-item-group');
  if (lineItemGroup) {
    lineItemGroup.style.display = expenseTrackerState.selectedCategory === 'unplanned' ? 'none' : '';
  }

  // Keep category and line item selected - update their displays with new data
  if (expenseTrackerState.selectedCategory && expenseTrackerState.selectedCategory !== 'unplanned') {
    updateCategoryProgress(expenseTrackerState.selectedCategory);
  }

  // Refresh remaining amount display if line item is selected
  if (expenseTrackerState.selectedLineItem) {
    showBudgetRemainingInfo();
  }

  // Reset affordability indicator
  updateAffordabilityIndicator();
}

/**
 * Utility functions
 */

async function getMonthlyExpenseTotal(budgetId, month) {
  try {
    const expenses = await getCachedMonthExpenses(budgetId, month);
    return expenses.reduce((total, expense) => {
      const amount = expense.amount || 0;
      return total + (expense.isReturn ? -amount : amount);
    }, 0);
  } catch (error) {
    console.error('Failed to get monthly expense total:', error);
    return 0;
  }
}

async function getScheduledRecurringTotal(month) {
  try {
    const recurringExpenses = await getCurrentMonthRecurringExpenses();
    
    // Apply current month filtering to fix multi-month cache issue
    const now = new Date();
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    
    const currentMonthExpenses = recurringExpenses.filter(expense => {
      if (!expense.dueDate) return false;
      const dueDate = new Date(expense.dueDate);
      return dueDate >= startOfMonth && dueDate <= endOfMonth;
    });
    
    const scheduledExpenses = currentMonthExpenses.filter(expense => expense.status === 'scheduled' || expense.status === 'pending');
    const total = scheduledExpenses.reduce((total, expense) => total + (expense.amount || 0), 0);
    
    console.log('💰 [Overall Scheduled Debug]:', {
      totalRecurringExpensesFromCache: recurringExpenses.length,
      filteredToCurrentMonth: currentMonthExpenses.length,
      scheduledExpenses: scheduledExpenses.length,
      scheduledTotal: total,
      dateRange: `${startOfMonth.toISOString()} to ${endOfMonth.toISOString()}`,
      scheduledBreakdown: scheduledExpenses.map(e => ({
        name: e.name || e.label,
        category: e.categoryName,
        amount: e.amount,
        dueDate: e.dueDate
      }))
    });
    
    return total;
  } catch (error) {
    console.error('Failed to get scheduled recurring total:', error);
    return 0;
  }
}

async function getCategoryBudgetRemaining(categoryId) {
  if (!expenseTrackerState.currentBudgetData.categories[categoryId]) {
    return 0;
  }
  
  const category = expenseTrackerState.currentBudgetData.categories[categoryId];
  let categoryBudget = 0;
  
  if (category.items && Array.isArray(category.items)) {
    category.items.forEach(item => {
      // Convert to monthly amount based on frequency
      const monthlyAmount = convertToMonthlyAmount(item.amount || 0, item.frequency || 'monthly');
      categoryBudget += monthlyAmount;
    });
  }
  
  // Subtract actual expenses for this category this month
  try {
    const currentMonth = new Date().toISOString().slice(0, 7);
    const budgetId = getCurrentBudgetId();
    const spent = await getCategoryExpenseTotal(budgetId, categoryId, currentMonth);
    const scheduledRecurring = await getCategoryScheduledRecurring(categoryId, currentMonth);
    
    return categoryBudget - spent - scheduledRecurring;
  } catch (error) {
    console.error('Error calculating category budget remaining:', error);
    return categoryBudget;
  }
}

async function getOverallBudgetRemaining() {
  let totalBudget = 0;
  
  if (expenseTrackerState.currentBudgetData.categories) {
    Object.values(expenseTrackerState.currentBudgetData.categories).forEach(category => {
      if (category.items && Array.isArray(category.items)) {
        category.items.forEach(item => {
          // Convert to monthly amount based on frequency
          const monthlyAmount = convertToMonthlyAmount(item.amount || 0, item.frequency || 'monthly');
          totalBudget += monthlyAmount;
        });
      }
    });
  }
  
  // Subtract actual expenses and scheduled recurring
  try {
    const currentMonth = new Date().toISOString().slice(0, 7);
    const budgetId = getCurrentBudgetId();
    const spent = await getMonthlyExpenseTotal(budgetId, currentMonth);
    const scheduledRecurring = await getScheduledRecurringTotal(currentMonth);
    
    return totalBudget - spent - scheduledRecurring;
  } catch (error) {
    console.error('Error calculating overall budget remaining:', error);
    return totalBudget;
  }
}

async function getCategoryExpenseTotal(budgetId, categoryId, month) {
  try {
    const expenses = await getCachedMonthExpenses(budgetId, month);
    return expenses
      .filter(exp => exp.categoryId === categoryId)
      .reduce((total, expense) => {
        const amount = expense.amount || 0;
        return total + (expense.isReturn ? -amount : amount);
      }, 0);
  } catch (error) {
    console.error('Failed to get category expense total:', error);
    return 0;
  }
}

async function getCategoryScheduledRecurring(categoryId, month) {
  try {
    const recurringExpenses = await getCurrentMonthRecurringExpenses();
    
    // Get the category name from the categoryId (array index)
    const category = expenseTrackerState.currentBudgetData.categories[categoryId];
    const categoryName = category?.name || '';
    
    console.log('getCategoryScheduledRecurring debug:', {
      categoryId,
      categoryName,
      month,
      totalRecurringExpenses: recurringExpenses.length,
      recurringExpenses: recurringExpenses.map(e => ({
        name: e.name || e.label,
        categoryName: e.categoryName,
        categoryId: e.categoryId,
        lineItemId: e.lineItemId,
        amount: e.amount,
        status: e.status,
        date: e.date,
        fullExpense: e  // Show the full object to see all properties
      }))
    });
    
    // Match by categoryName since that's what the recurring system uses
    const filtered = recurringExpenses
      .filter(expense => {
        const categoryMatch = expense.categoryName === categoryName;
        const statusMatch = expense.status === 'scheduled' || expense.status === 'pending';
        
        // Only log Housing category matches to reduce noise
        if (categoryName === '🏠 Housing' && categoryMatch) {
          console.log('🏠 [Housing Filter Debug]:', {
            expenseName: expense.name || expense.label,
            amount: expense.amount,
            status: expense.status,
            dueDate: expense.dueDate,
            id: expense.id || 'no-id',
            recurringId: expense.recurringId,
            categoryName: expense.categoryName,
            statusMatch,
            willInclude: categoryMatch && statusMatch,
            fullExpenseObject: expense // Show full object to see all properties
          });
        }
        
        return categoryMatch && statusMatch;
      });
    
    console.log('Filtered recurring expenses for category:', {
      categoryId,
      categoryName,
      filteredCount: filtered.length,
      filtered: filtered.map(e => ({
        name: e.name || e.label,
        amount: e.amount,
        status: e.status
      }))
    });
    
    return filtered.reduce((total, expense) => total + (expense.amount || 0), 0);
  } catch (error) {
    console.error('Failed to get category scheduled recurring total:', error);
    return 0;
  }
}

async function updateMonthlyTotals(expenseData) {
  // This would typically be handled by a Cloud Function
  // Implementation would update the monthlyTotals document
  console.log('Monthly totals update needed for:', expenseData);
}

async function updateCategoryProgress(categoryId) {
  const progressSection = document.getElementById('category-progress-section');
  const titleElement = document.getElementById('category-progress-title');
  const progressFill = document.getElementById('category-progress-fill');
  const progressText = document.getElementById('category-progress-text');
  const categoryRemaining = document.getElementById('category-remaining');
  
  if (!progressSection || !expenseTrackerState.currentBudgetData.categories[categoryId]) {
    if (progressSection) progressSection.style.display = 'none';
    return;
  }
  
  const category = expenseTrackerState.currentBudgetData.categories[categoryId];
  
  try {
    // Calculate category budget
    let categoryBudget = 0;
    if (category.items && Array.isArray(category.items)) {
      category.items.forEach(item => {
        const monthlyAmount = convertToMonthlyAmount(item.amount || 0, item.frequency || 'monthly');
        categoryBudget += monthlyAmount;
      });
    }
    
    // Get spent and scheduled amounts for this category
    const currentMonth = new Date().toISOString().slice(0, 7);
    const budgetId = getCurrentBudgetId();
    const spent = await getCategoryExpenseTotal(budgetId, categoryId, currentMonth);
    const scheduledRecurring = await getCategoryScheduledRecurring(categoryId, currentMonth);
    
    console.log('Category progress debug:', {
      categoryId,
      categoryName: category.name,
      spent,
      scheduledRecurring,
      currentMonth
    });
    
    const totalAllocated = spent + scheduledRecurring;
    const remaining = categoryBudget - totalAllocated;
    const progressPercentage = categoryBudget > 0 ? (spent / categoryBudget) * 100 : 0;
    
    // DEBUG: Show the calculation breakdown
    console.log('🔍 [Category Debug] Breakdown:', {
      categoryName: category.name,
      categoryBudget,
      spent,
      scheduledRecurring,
      totalAllocated,
      remaining,
      items: category.items?.map(item => ({
        label: item.label,
        amount: item.amount,
        frequency: item.frequency,
        monthlyAmount: convertToMonthlyAmount(item.amount || 0, item.frequency || 'monthly')
      }))
    });
    
    // Update category title
    if (titleElement) {
      titleElement.textContent = `${category.name || 'Category'} Progress`;
    }
    
    // Update progress bar with stacked display
    const categoryScheduledFill = document.getElementById('category-progress-scheduled-fill');
    const scheduledPercentage = categoryBudget > 0 ? (scheduledRecurring / categoryBudget) * 100 : 0;
    
    console.log('🎨 [Category Progress Bar Debug]:', {
      categoryName: category.name,
      progressFill: !!progressFill,
      categoryScheduledFill: !!categoryScheduledFill,
      progressPercentage,
      scheduledPercentage,
      categoryBudget,
      scheduledRecurring
    });
    
    if (progressFill) {
      // Actual spending fills first (darker color)
      progressFill.style.width = `${Math.min(progressPercentage, 100)}%`;
      progressFill.className = 'progress-fill';
      
      // Apply correct border-radius based on whether scheduled amount exists
      if (scheduledPercentage > 0) {
        progressFill.classList.add('left-rounded');
      } else {
        progressFill.classList.add('full-rounded');
      }
      
      if (progressPercentage > 100) {
        progressFill.classList.add('danger');
      } else if (progressPercentage > 80) {
        progressFill.classList.add('warning');
      }
    }
    
    if (categoryScheduledFill) {
      // Scheduled expenses show as separate section (lighter color)
      const totalPercentage = progressPercentage + scheduledPercentage;
      const cappedTotal = Math.min(totalPercentage, 100);
      const cappedActual = Math.min(progressPercentage, 100);
      const scheduledWidth = cappedTotal - cappedActual;
      
      const categoryScheduledWidthPercent = Math.max(scheduledWidth, 0);
      
      console.log('🎨 [Category Scheduled Fill Debug]:', {
        categoryName: category.name,
        totalPercentage,
        cappedTotal,
        cappedActual,
        scheduledWidth,
        categoryScheduledWidthPercent,
        leftPosition: `${cappedActual}%`
      });
      
      categoryScheduledFill.style.setProperty('--scheduled-width', `${categoryScheduledWidthPercent}%`);
      categoryScheduledFill.style.width = `${categoryScheduledWidthPercent}%`;
      categoryScheduledFill.style.left = `${cappedActual}%`;
      categoryScheduledFill.style.position = 'absolute';
      categoryScheduledFill.style.height = '100%';
      categoryScheduledFill.className = 'progress-scheduled';
      
      // Apply correct border-radius based on whether spent amount exists
      if (progressPercentage > 0) {
        categoryScheduledFill.classList.add('right-rounded');
      } else {
        categoryScheduledFill.classList.add('full-rounded');
      }
      
      // Setup tooltip for category progress bar
      setupCategoryProgressTooltip(categoryScheduledFill, categoryId);
    }
    
    // Update progress text
    if (progressText) {
      progressText.textContent = `$${spent.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} spent + $${scheduledRecurring.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} scheduled of $${categoryBudget.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} budgeted`;
    }
    
    // Update remaining amount
    if (categoryRemaining) {
      if (remaining < 0) {
        categoryRemaining.textContent = `$${Math.abs(remaining).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} over budget`;
        categoryRemaining.className = 'category-remaining danger';
      } else {
        categoryRemaining.textContent = `$${remaining.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} remaining`;
        categoryRemaining.className = 'category-remaining';
        
        if (remaining < categoryBudget * 0.1) {
          categoryRemaining.classList.add('warning');
        }
      }
    }
    
    // Show the progress section
    progressSection.style.display = 'block';
    
  } catch (error) {
    console.error('Error updating category progress:', error);
    if (progressSection) progressSection.style.display = 'none';
  }
}

function showCategoryProgress() {
  const progressSection = document.getElementById('category-progress-section');
  if (progressSection) {
    progressSection.style.display = 'block';
  }
}

function hideCategoryProgress() {
  const progressSection = document.getElementById('category-progress-section');
  if (progressSection) {
    progressSection.style.display = 'none';
  }
}

async function checkForDuplicateRecurring(formData) {
  try {
    // Check against upcoming recurring expenses
    const matches = expenseTrackerState.upcomingRecurring.filter(recurring => {
      // Check if category and line item match
      const categoryMatch = recurring.categoryId === formData.categoryId;
      const lineItemMatch = recurring.lineItemId === formData.lineItemId;
      
      // Check if amount is within 20% tolerance
      const amountTolerance = recurring.amount * 0.2;
      const amountMatch = Math.abs(recurring.amount - formData.amount) <= amountTolerance;
      
      // Check if date is within reasonable range (within 7 days)
      const recurringDate = new Date(recurring.date);
      const today = new Date();
      const daysDiff = Math.abs((recurringDate - today) / (1000 * 60 * 60 * 24));
      const dateMatch = daysDiff <= 7;
      
      console.log('Checking recurring match:', {
        recurringName: recurring.name,
        categoryMatch,
        lineItemMatch,
        amountMatch,
        dateMatch,
        recurringAmount: recurring.amount,
        formAmount: formData.amount,
        daysDiff
      });
      
      return categoryMatch && lineItemMatch && amountMatch && dateMatch;
    });
    
    console.log('Duplicate check results:', {
      totalRecurring: expenseTrackerState.upcomingRecurring.length,
      matches: matches.length,
      matchDetails: matches.map(m => ({ name: m.name, amount: m.amount, date: m.date }))
    });
    
    return {
      hasDuplicate: matches.length > 0,
      matches
    };
  } catch (error) {
    console.error('Error checking for duplicate recurring expenses:', error);
    return {
      hasDuplicate: false,
      matches: []
    };
  }
}

async function showDuplicateWarningModal(matches) {
  return new Promise((resolve) => {
    const modal = document.getElementById('duplicate-warning-modal');
    const messageEl = document.getElementById('duplicate-warning-message');
    const markPaidBtn = document.getElementById('mark-recurring-paid');
    const continueBtn = document.getElementById('continue-separate');
    const cancelBtn = document.getElementById('cancel-expense');
    
    if (!modal) {
      // Fallback to confirm dialog
      const firstMatch = matches[0];
      const confirmed = confirm(
        `This looks like your recurring "${firstMatch.name}" for $${firstMatch.amount.toFixed(2)}. ` +
        `Mark the recurring expense as paid?`
      );
      
      resolve({
        action: confirmed ? 'link' : 'continue',
        recurringId: confirmed ? firstMatch.id : null
      });
      return;
    }
    
    // Update modal content
    const firstMatch = matches[0];
    const dueDate = new Date(firstMatch.date);
    const isToday = dueDate.toDateString() === new Date().toDateString();
    const dueDateText = isToday ? 'due today' : `due ${dueDate.toLocaleDateString()}`;
    
    if (messageEl) {
      messageEl.textContent = `This looks like your recurring "${firstMatch.name}" for $${firstMatch.amount.toFixed(2)} ${dueDateText}.`;
    }
    
    // Setup button handlers
    const handleMarkPaid = () => {
      cleanupModal();
      resolve({
        action: 'link',
        recurringId: firstMatch.id
      });
    };
    
    const handleContinue = () => {
      cleanupModal();
      resolve({
        action: 'continue',
        recurringId: null
      });
    };
    
    const handleCancel = () => {
      cleanupModal();
      resolve({
        action: 'cancel',
        recurringId: null
      });
    };
    
    const cleanupModal = () => {
      modal.style.display = 'none';
      document.body.style.overflow = '';
      
      // Remove event listeners
      markPaidBtn?.removeEventListener('click', handleMarkPaid);
      continueBtn?.removeEventListener('click', handleContinue);
      cancelBtn?.removeEventListener('click', handleCancel);
    };
    
    // Add event listeners
    markPaidBtn?.addEventListener('click', handleMarkPaid);
    continueBtn?.addEventListener('click', handleContinue);
    cancelBtn?.addEventListener('click', handleCancel);
    
    // Reset scroll position to top
    window.scrollTo(0, 0);
    
    // Show modal
    modal.style.display = 'flex';
    document.body.style.overflow = 'hidden';
    
    // Add global function for modal close button
    window.closeDuplicateWarningModal = handleCancel;
  });
}

function showError(message) {
  // This would show a proper error notification
  // For now, use alert
  alert(`Error: ${message}`);
}

function showSuccess(message) {
  // This would show a proper success notification
  // For now, use a temporary message
  console.log(`Success: ${message}`);
  
  // Could show a temporary success indicator
  const indicator = document.getElementById('affordability-indicator');
  if (indicator) {
    const originalClass = indicator.className;
    const originalIcon = indicator.querySelector('.indicator-icon')?.textContent;
    const originalText = indicator.querySelector('.indicator-text')?.textContent;
    
    indicator.className = 'affordability-indicator good';
    if (indicator.querySelector('.indicator-icon')) {
      indicator.querySelector('.indicator-icon').textContent = '✅';
    }
    if (indicator.querySelector('.indicator-text')) {
      indicator.querySelector('.indicator-text').textContent = 'Added!';
    }
    
    setTimeout(() => {
      indicator.className = originalClass;
      if (indicator.querySelector('.indicator-icon')) {
        indicator.querySelector('.indicator-icon').textContent = originalIcon;
      }
      if (indicator.querySelector('.indicator-text')) {
        indicator.querySelector('.indicator-text').textContent = originalText;
      }
    }, 2000);
  }
}

/**
 * Setup tooltip functionality for overall progress bar
 * Shows pending expenses grouped by category
 */
function setupOverallProgressTooltip(scheduledFill) {
  const tooltip = document.getElementById('overall-progress-tooltip');
  const tooltipContent = document.getElementById('overall-tooltip-content');
  
  if (!scheduledFill || !tooltip || !tooltipContent) return;
  
  let hideTimeout;
  
  const showTooltip = async (e) => {
    if (hideTimeout) {
      clearTimeout(hideTimeout);
      hideTimeout = null;
    }
    
    try {
      // Get all pending recurring expenses from the same source as progress bars
      const currentMonth = new Date().toISOString().slice(0, 7);
      const pendingByCategory = {};
      
      // Get recurring expenses from the budget system
      const allRecurringExpenses = await getCurrentMonthRecurringExpenses();
      
      allRecurringExpenses.forEach(expense => {
        
        if (expense.status === 'pending') {
          const categoryName = expense.categoryName || 'Other';
          if (!pendingByCategory[categoryName]) {
            pendingByCategory[categoryName] = [];
          }
          pendingByCategory[categoryName].push(expense);
        }
      });
      
      // Build tooltip content
      let content = '';
      const categories = Object.keys(pendingByCategory).sort();
      
      if (categories.length === 0) {
        content = '<div class="tooltip-item">No pending expenses</div>';
      } else {
        categories.forEach(categoryName => {
          const expenses = pendingByCategory[categoryName];
          const categoryTotal = expenses.reduce((sum, exp) => sum + (exp.amount || 0), 0);
          
          content += `<div class="tooltip-item">
            <span class="tooltip-item-category">${categoryName}</span>
            <span class="tooltip-item-amount">$${categoryTotal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
          </div>`;
        });
      }
      
      tooltipContent.innerHTML = content;
      
      // Position tooltip
      const rect = scheduledFill.getBoundingClientRect();
      const containerRect = scheduledFill.parentElement.getBoundingClientRect();
      
      tooltip.style.left = `${Math.min(50, (rect.left + rect.width/2 - containerRect.left) / containerRect.width * 100)}%`;
      tooltip.classList.add('show');
      
    } catch (error) {
      console.error('Error showing overall progress tooltip:', error);
    }
  };
  
  const hideTooltip = () => {
    hideTimeout = setTimeout(() => {
      tooltip.classList.remove('show');
    }, 150);
  };
  
  // Add event listeners
  scheduledFill.addEventListener('mouseenter', showTooltip);
  scheduledFill.addEventListener('mouseleave', hideTooltip);
  
  // Keep tooltip visible when hovering over it
  tooltip.addEventListener('mouseenter', () => {
    if (hideTimeout) {
      clearTimeout(hideTimeout);
      hideTimeout = null;
    }
  });
  
  tooltip.addEventListener('mouseleave', hideTooltip);
}

/**
 * Setup tooltip functionality for category progress bar  
 * Shows pending line items within the selected category
 */
function setupCategoryProgressTooltip(scheduledFill, categoryId) {
  const tooltip = document.getElementById('category-progress-tooltip');
  const tooltipContent = document.getElementById('category-tooltip-content');
  
  if (!scheduledFill || !tooltip || !tooltipContent || !categoryId) return;
  
  let hideTimeout;
  
  const showTooltip = async (e) => {
    if (hideTimeout) {
      clearTimeout(hideTimeout);
      hideTimeout = null;
    }
    
    try {
      const category = expenseTrackerState.currentBudgetData.categories[categoryId];
      const categoryName = category?.name || '';
      
      // Get pending recurring expenses for this category from the same source as progress bars
      const allRecurringExpenses = await getCurrentMonthRecurringExpenses();
      const categoryPending = allRecurringExpenses.filter(expense => 
        expense.status === 'pending' &&
        expense.categoryName === categoryName
      );
      
      // Group by line item
      const pendingByLineItem = {};
      
      categoryPending.forEach(expense => {
        const lineItemName = expense.name || expense.label || 'Other';
        if (!pendingByLineItem[lineItemName]) {
          pendingByLineItem[lineItemName] = [];
        }
        pendingByLineItem[lineItemName].push(expense);
      });
      
      // Build tooltip content
      let content = '';
      const lineItems = Object.keys(pendingByLineItem).sort();
      
      if (lineItems.length === 0) {
        content = '<div class="tooltip-item">No pending line items</div>';
      } else {
        lineItems.forEach(lineItemName => {
          const expenses = pendingByLineItem[lineItemName];
          const lineItemTotal = expenses.reduce((sum, exp) => sum + (exp.amount || 0), 0);
          
          content += `<div class="tooltip-item">
            <span class="tooltip-item-name">${lineItemName}</span>
            <span class="tooltip-item-amount">$${lineItemTotal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
          </div>`;
          
          // Show due dates if multiple occurrences
          if (expenses.length > 1) {
            expenses.forEach(expense => {
              const dueDate = new Date(expense.date);
              const dueDateText = dueDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
              content += `<div class="tooltip-item" style="margin-left: 12px; font-size: 0.85em; opacity: 0.8;">
                <span class="tooltip-item-date">${dueDateText}</span>
                <span class="tooltip-item-amount">$${(expense.amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
              </div>`;
            });
          }
        });
      }
      
      tooltipContent.innerHTML = content;
      
      // Position tooltip
      const rect = scheduledFill.getBoundingClientRect();
      const containerRect = scheduledFill.parentElement.getBoundingClientRect();
      
      tooltip.style.left = `${Math.min(50, (rect.left + rect.width/2 - containerRect.left) / containerRect.width * 100)}%`;
      tooltip.classList.add('show');
      
    } catch (error) {
      console.error('Error showing category progress tooltip:', error);
    }
  };
  
  const hideTooltip = () => {
    hideTimeout = setTimeout(() => {
      tooltip.classList.remove('show');
    }, 150);
  };
  
  // Add event listeners
  scheduledFill.addEventListener('mouseenter', showTooltip);
  scheduledFill.addEventListener('mouseleave', hideTooltip);
  
  // Keep tooltip visible when hovering over it
  tooltip.addEventListener('mouseenter', () => {
    if (hideTimeout) {
      clearTimeout(hideTimeout);
      hideTimeout = null;
    }
  });
  
  tooltip.addEventListener('mouseleave', hideTooltip);
}



/**
 * Setup custom date picker functionality
 */
function setupDatePicker() {
  const calendarBtn = document.getElementById('expense-date-btn');
  const datePickerPopup = document.getElementById('date-picker-popup');
  const datePickerTitle = document.getElementById('date-picker-title');
  const prevMonthBtn = document.getElementById('prev-month');
  const nextMonthBtn = document.getElementById('next-month');
  const datePickerDays = document.getElementById('date-picker-days');
  const dateInput = document.getElementById('expense-date-input');
  
  if (!calendarBtn || !datePickerPopup || !dateInput) return;
  
  let currentMonth = new Date();
  let selectedDate = new Date();
  
  // Initialize with today's date
  dateInput.value = formatDateToLocal(selectedDate);
  
  // Show/hide date picker
  const showDatePicker = () => {
    updateCalendar();
    datePickerPopup.classList.add('active');
  };
  
  const hideDatePicker = () => {
    datePickerPopup.classList.remove('active');
  };
  
  // Update calendar display
  const updateCalendar = () => {
    const monthNames = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    
    // Update title
    datePickerTitle.textContent = `${monthNames[currentMonth.getMonth()]} ${currentMonth.getFullYear()}`;
    
    // Clear previous days
    datePickerDays.innerHTML = '';
    
    // Add day headers
    const dayHeaders = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
    dayHeaders.forEach(header => {
      const headerElement = document.createElement('div');
      headerElement.className = 'date-day-header';
      headerElement.textContent = header;
      headerElement.style.fontWeight = '600';
      headerElement.style.color = 'var(--text-light)';
      headerElement.style.fontSize = 'var(--font-xs)';
      headerElement.style.padding = 'var(--spacing-xs)';
      headerElement.style.textAlign = 'center';
      datePickerDays.appendChild(headerElement);
    });
    
    // Get first day of month and number of days
    const firstDay = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
    const lastDay = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0);
    const startingDayOfWeek = firstDay.getDay();
    const daysInMonth = lastDay.getDate();
    
    // Add empty cells for days before month starts
    for (let i = 0; i < startingDayOfWeek; i++) {
      const emptyDay = document.createElement('div');
      emptyDay.className = 'date-day disabled';
      datePickerDays.appendChild(emptyDay);
    }
    
    // Add days of the month
    const today = new Date();
    const todayStr = formatDateToLocal(today);
    const selectedStr = formatDateToLocal(selectedDate);
    
    for (let day = 1; day <= daysInMonth; day++) {
      const dayElement = document.createElement('div');
      dayElement.className = 'date-day';
      dayElement.textContent = day;
      
      const dayDate = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), day);
      const dayStr = formatDateToLocal(dayDate);
      
      // Disable future dates
      if (dayDate > today) {
        dayElement.classList.add('disabled');
      } else {
        dayElement.addEventListener('click', () => selectDate(dayDate));
      }
      
      // Highlight today
      if (dayStr === todayStr) {
        dayElement.classList.add('today');
      }
      
      // Highlight selected date
      if (dayStr === selectedStr) {
        dayElement.classList.add('selected');
      }
      
      datePickerDays.appendChild(dayElement);
    }
  };
  
  // Select a date
  const selectDate = (date) => {
    selectedDate = new Date(date);
    dateInput.value = selectedDate.toISOString().split('T')[0];
    hideDatePicker();
  };
  
  // Navigate months
  const navigateMonth = (direction) => {
    currentMonth.setMonth(currentMonth.getMonth() + direction);
    
    // Don't allow navigation to future months
    const today = new Date();
    if (currentMonth.getFullYear() > today.getFullYear() || 
        (currentMonth.getFullYear() === today.getFullYear() && currentMonth.getMonth() > today.getMonth())) {
      currentMonth.setMonth(today.getMonth());
      currentMonth.setFullYear(today.getFullYear());
    }
    
    updateCalendar();
  };
  
  // Event listeners
  calendarBtn.addEventListener('click', (e) => {
    e.preventDefault();
    e.stopPropagation();
    showDatePicker();
  });
  
  prevMonthBtn?.addEventListener('click', () => navigateMonth(-1));
  nextMonthBtn?.addEventListener('click', () => navigateMonth(1));
  
  // Click outside to close
  document.addEventListener('click', (e) => {
    if (!datePickerPopup.contains(e.target) && !calendarBtn.contains(e.target)) {
      hideDatePicker();
    }
  });
  
  // Initialize calendar
  updateCalendar();
}

/**
 * Delete an expense and update the UI
 */
async function deleteExpense(expenseId) {
  if (!expenseId) {
    console.error('No expense ID provided for deletion');
    return;
  }

  // Show confirmation dialog
  const confirmed = confirm('Are you sure you want to delete this expense? This action cannot be undone.');
  if (!confirmed) {
    return;
  }

  try {
    // Get current user
    const user = getCurrentUser();
    if (!user) {
      throw new Error('No user authenticated');
    }
    
    // Use shared account logic
    const linkState = getLinkingState();
    const targetUserId = linkState.isLinkedAccount ? linkState.linkedAccountId : user.uid;
    
    const db = getFirestore();
    const expenseDocRef = doc(db, `users/${targetUserId}/expenses`, expenseId);
    
    // Delete the expense document
    await deleteDoc(expenseDocRef);
    
    // Clear expense cache to ensure fresh data on next load
    if (typeof window !== 'undefined' && window.clearExpenseCache) {
      window.clearExpenseCache();
    }
    
    console.log('Expense deleted successfully:', expenseId);
    
    // Refresh the expense details to reflect the deletion
    await loadCategoryExpenseDetails();
    
    // Update progress bars to reflect the change
    await updateCategoryProgress(expenseTrackerState.selectedCategory);
    await updateOverallProgress();
    
    // Show success feedback
    showSuccess('Expense deleted successfully');
    
  } catch (error) {
    console.error('Error deleting expense:', error);
    showError('Failed to delete expense. Please try again.');
  }
}

/**
 * ============================================================================
 * EXPENSE HISTORY FUNCTIONALITY FOR EXPENSE TRACKER
 * ============================================================================
 */

/**
 * Initialize expense history section in expense tracker
 */
export function initializeExpenseTrackerHistory() {
  console.log('Initializing expense tracker history...');
  
  // Set default date range (past 7 days)
  setExpenseTrackerDefaultDateRange();
  
  // Setup event listeners
  setupExpenseTrackerHistoryEventListeners();
  
  // Update header with initial date range
  updateExpenseTrackerHistoryHeader();
  
  // Load initial data
  loadExpenseTrackerHistory();
}

/**
 * Set default date range to past 7 days for expense tracker
 */
function setExpenseTrackerDefaultDateRange() {
  const today = new Date();
  const sevenDaysAgo = new Date();
  sevenDaysAgo.setDate(today.getDate() - 7);
  
  const fromInput = document.getElementById('expense-tracker-history-from');
  const toInput = document.getElementById('expense-tracker-history-to');
  
  if (fromInput) {
    fromInput.value = formatDateForInput(sevenDaysAgo);
  }
  
  if (toInput) {
    toInput.value = formatDateForInput(today);
  }
}

/**
 * Format date for input element (YYYY-MM-DD)
 */
function formatDateForInput(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Setup event listeners for expense tracker history controls
 */
function setupExpenseTrackerHistoryEventListeners() {
  const fromInput = document.getElementById('expense-tracker-history-from');
  const toInput = document.getElementById('expense-tracker-history-to');
  
  if (fromInput) {
    fromInput.addEventListener('change', () => loadExpenseTrackerHistory());
  }

  if (toInput) {
    toInput.addEventListener('change', () => loadExpenseTrackerHistory());
  }

  // Populate and wire up budget dropdown
  const budgetSelect = document.getElementById('expense-history-budget-select');
  if (budgetSelect) {
    const versions = getBudgetVersions();
    const user = getCurrentUser();
    if (versions && user) {
      // Clear existing options except "All Budgets"
      while (budgetSelect.options.length > 1) budgetSelect.remove(1);
      const linkState = getLinkingState();
      const targetUserId = linkState.isLinkedAccount ? linkState.linkedAccountId : user.uid;
      versions.forEach(version => {
        const option = document.createElement('option');
        option.value = `${targetUserId}_${version.name}`;
        option.textContent = version.name;
        budgetSelect.appendChild(option);
      });
    }
    budgetSelect.addEventListener('change', () => loadExpenseTrackerHistory());
  }
}

/**
 * Load and display expense history for the selected date range in expense tracker
 * @param {string} filterCategory - Optional category name to filter by
 */
export async function loadExpenseTrackerHistory(filterCategory = null) {
  console.log('Loading expense tracker history...', filterCategory ? `Filtering by: ${filterCategory}` : '');
  
  try {
    const fromInput = document.getElementById('expense-tracker-history-from');
    const toInput = document.getElementById('expense-tracker-history-to');
    const contentElement = document.getElementById('expense-tracker-history-content');
    
    if (!fromInput || !toInput || !contentElement) {
      console.error('Required elements not found for expense tracker history');
      return;
    }
    
    const fromDate = new Date(fromInput.value);
    const displayToDate = new Date(toInput.value);
    
    // Create query date with an extra day to ensure today's expenses are included
    const queryToDate = new Date(toInput.value);
    queryToDate.setDate(queryToDate.getDate() + 1);
    queryToDate.setHours(23, 59, 59, 999);
    
    // Validate date range (using display date for validation)
    if (fromDate > displayToDate) {
      showExpenseTrackerHistoryError('From date cannot be after To date');
      return;
    }
    
    // Show loading state
    contentElement.innerHTML = `
      <div class="expense-history-loading">
        <div class="loading-spinner"></div>
        <div>Loading expenses...</div>
      </div>
    `;
    
    // Get current user and budget
    const user = getCurrentUser();
    if (!user) {
      showExpenseTrackerHistoryError('User not authenticated');
      return;
    }
    
    const currentBudgetId = getCurrentBudgetId();
    if (!currentBudgetId) {
      showExpenseTrackerHistoryError('No budget selected');
      return;
    }
    
    // Convert dates to Firestore Timestamps for proper comparison
    const fromTimestamp = Timestamp.fromDate(fromDate);
    const toTimestamp = Timestamp.fromDate(queryToDate);

    // Budget filter dropdown
    const budgetSelect = document.getElementById('expense-history-budget-select');
    const selectedBudgetId = budgetSelect?.value || '';

    // Fetch expenses, optionally filtered by budget
    const expensesQuery = selectedBudgetId
      ? query(
          getUserExpenseCollection(),
          where('budgetId', '==', selectedBudgetId),
          where('date', '>=', fromTimestamp),
          where('date', '<=', toTimestamp),
          orderBy('date', 'desc')
        )
      : query(
          getUserExpenseCollection(),
          where('date', '>=', fromTimestamp),
          where('date', '<=', toTimestamp),
          orderBy('date', 'desc')
        );
    
    const snapshot = await getDocs(expensesQuery);
    let expenses = [];
    
    snapshot.forEach(doc => {
      expenses.push({
        id: doc.id,
        ...doc.data()
      });
    });
    
    // Filter by category if specified
    if (filterCategory) {
      const budgetData = getCurrentBudgetData();
      
      // If filterCategory is a number (categoryId), use it directly
      // If it's a name, try to find the corresponding categoryId
      let targetCategoryId = null;
      let targetCategoryName = null;
      
      if (!isNaN(filterCategory)) {
        // filterCategory is a categoryId
        targetCategoryId = filterCategory.toString();
        if (budgetData && budgetData.categories && budgetData.categories[targetCategoryId]) {
          const category = budgetData.categories[targetCategoryId];
          targetCategoryName = category.name ? category.name.replace(/^[^\w\s]+\s*/, '').trim() : category.name;
        }
      } else {
        // filterCategory is a category name, find the corresponding categoryId
        targetCategoryName = filterCategory;
        const cleanedFilterCategory = filterCategory.replace(/^[^\w\s]+\s*/, '').trim() || filterCategory;
        
        if (budgetData && budgetData.categories) {
          for (const [categoryId, category] of Object.entries(budgetData.categories)) {
            if (category && category.name) {
              const cleanedName = category.name.replace(/^[^\w\s]+\s*/, '').trim() || category.name;
              
              console.log('🔍 [Name Match Debug]:', {
                categoryId,
                originalName: category.name,
                cleanedName,
                filterCategory,
                cleanedFilterCategory,
                directMatch: category.name === filterCategory,
                cleanedMatch: cleanedName === cleanedFilterCategory
              });
              
              if (cleanedName === cleanedFilterCategory || category.name === filterCategory) {
                console.log('✅ Found categoryId match:', categoryId);
                targetCategoryId = categoryId;
                break;
              }
            }
          }
        }
      }
      
      console.log('🔍 [Filter Logic Debug] Filtering expenses:', {
        filterCategory,
        isNaN_filterCategory: isNaN(filterCategory),
        targetCategoryId,
        targetCategoryName,
        totalExpenses: expenses.length
      });
      
      expenses = expenses.filter(expense => {
        console.log('🔍 [Filter Match Debug] Checking expense:', {
          expenseId: expense.id,
          lineItemName: expense.lineItemName,
          categoryName: expense.categoryName,
          categoryId: expense.categoryId,
          targetCategoryId,
          targetCategoryName
        });
        
        // Try to match by categoryId first
        if (targetCategoryId !== null && expense.categoryId !== undefined) {
          const match = expense.categoryId.toString() === targetCategoryId;
          console.log('🔍 [CategoryId Match]:', {
            expenseCategoryId: expense.categoryId,
            targetCategoryId,
            match
          });
          if (match) {
            console.log('✅ Matched by categoryId:', expense.categoryId, expense.lineItemName);
            return true;
          }
        }
        
        // Fallback to categoryName match
        if (targetCategoryName && expense.categoryName) {
          const match = expense.categoryName === targetCategoryName;
          console.log('🔍 [CategoryName Match]:', {
            expenseCategoryName: expense.categoryName,
            targetCategoryName,
            match
          });
          if (match) {
            console.log('✅ Matched by categoryName:', expense.categoryName, expense.lineItemName);
            return true;
          }
        }
        
        return false;
      });
      
      console.log('Filtered expenses count:', expenses.length);
    }
    
    // Update header to show filter status
    updateExpenseTrackerHistoryHeader(filterCategory);
    
    if (expenses.length === 0) {
      showExpenseTrackerHistoryEmpty(filterCategory);
      return;
    }
    
    // Render expense history items
    renderExpenseTrackerHistoryItems(expenses);
    
  } catch (error) {
    console.error('Error loading expense tracker history:', error);
    showExpenseTrackerHistoryError('Failed to load expense history');
  }
}

/**
 * Render expense history items for expense tracker
 */
function renderExpenseTrackerHistoryItems(expenses) {
  const contentElement = document.getElementById('expense-tracker-history-content');
  if (!contentElement) return;
  
  // Sort expenses by date (newest first)
  const sortedExpenses = expenses.sort((a, b) => {
    const dateA = a.date.toDate ? a.date.toDate() : new Date(a.date);
    const dateB = b.date.toDate ? b.date.toDate() : new Date(b.date);
    return dateB - dateA;
  });
  
  const expenseItems = sortedExpenses.map(expense => {
    const expenseDate = expense.date.toDate ? expense.date.toDate() : new Date(expense.date);
    const formattedDate = expenseDate.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });

    // Get category name - check both categoryName and categoryId
    let categoryName = expense.categoryName || 'Uncategorized';

    // If no categoryName but we have categoryId, look it up from budget data
    if ((!expense.categoryName || expense.categoryName === 'Uncategorized') && expense.categoryId !== undefined) {
      const budgetData = getCurrentBudgetData();
      if (budgetData && budgetData.categories) {
        const categoryIndex = parseInt(expense.categoryId);
        const category = budgetData.categories[categoryIndex];
        if (category && category.name) {
          // Clean the category name (remove emoji prefixes)
          categoryName = category.name.replace(/^[^\w\s]+\s*/, '').trim() || category.name;
        }
      }
    }

    // Determine if this is a return
    const isReturn = expense.isReturn === true;
    const returnClass = isReturn ? ' expense-return' : '';
    const returnBadge = isReturn ? '<span class="return-badge"><svg xmlns="http://www.w3.org/2000/svg" width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" style="margin-right:3px"><path d="M9 14 4 9l5-5"/><path d="M4 9h10.5a5.5 5.5 0 0 1 5.5 5.5v0a5.5 5.5 0 0 1-5.5 5.5H11"/></svg>Return</span>' : '';

    return `
      <div class="expense-history-item${returnClass}" data-expense-id="${expense.id}">
        <div class="expense-item-info">
          <div class="expense-item-description">
            ${expense.lineItemName || 'Expense'}
            ${returnBadge}
          </div>
          <div class="expense-item-meta">
            <span class="expense-item-category">${expense.description || ''}</span>
            <span class="expense-item-date">${formattedDate}</span>
          </div>
        </div>
        <div class="expense-item-right">
          <div class="expense-item-amount${returnClass}">${formatCurrency(expense.amount)}</div>
          <div class="expense-item-actions">
            <button class="expense-action-btn edit" onclick="editExpenseFromTrackerHistory('${expense.id}')" title="Edit expense">
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21.174 6.812a1 1 0 0 0-3.986-3.987L3.842 16.174a2 2 0 0 0-.5.83l-1.321 4.352a.5.5 0 0 0 .623.622l4.353-1.32a2 2 0 0 0 .83-.497z"/><path d="m15 5 4 4"/></svg>
            </button>
            <button class="expense-action-btn delete" onclick="deleteExpenseFromTrackerHistory('${expense.id}')" title="Delete expense">
              ✕
            </button>
          </div>
        </div>
      </div>
    `;
  }).join('');
  
  contentElement.innerHTML = expenseItems;
}

/**
 * Show empty state for expense tracker history
 */
function showExpenseTrackerHistoryEmpty(filterCategory = null) {
  const contentElement = document.getElementById('expense-tracker-history-content');
  if (!contentElement) return;
  
  // Get the display name for the category
  let displayName = filterCategory;
  if (filterCategory && !isNaN(filterCategory)) {
    const budgetData = getCurrentBudgetData();
    if (budgetData && budgetData.categories && budgetData.categories[filterCategory]) {
      const category = budgetData.categories[filterCategory];
      if (category && category.name) {
        displayName = category.name.replace(/^[^\w\s]+\s*/, '').trim() || category.name;
      }
    }
  }
  
  const message = filterCategory
    ? `Nothing logged for "${displayName}" in this time period`
    : 'Nothing logged for this period';

  const submessage = filterCategory
    ? 'Try removing the filter or adjusting your date range'
    : 'Try a different date range, or start logging to see your entries here';
  
  contentElement.innerHTML = `
    <div class="expense-history-empty">
      <div class="empty-icon">📋</div>
      <div class="empty-message">${message}</div>
      <div class="empty-submessage">${submessage}</div>
      ${filterCategory ? `<button class="clear-filter-btn" onclick="clearExpenseTrackerHistoryFilter()">Clear Filter</button>` : ''}
    </div>
  `;
}

/**
 * Show error state for expense tracker history
 */
function showExpenseTrackerHistoryError(message) {
  const contentElement = document.getElementById('expense-tracker-history-content');
  if (!contentElement) return;
  
  contentElement.innerHTML = `
    <div class="expense-history-empty">
      <div class="empty-icon">⚠️</div>
      <div class="empty-message">Error Loading Expenses</div>
      <div class="empty-submessage">${message}</div>
    </div>
  `;
}

/**
 * Update expense tracker history header to show filter status and date range
 */
function updateExpenseTrackerHistoryHeader(filterCategory = null) {
  const headerElement = document.querySelector('.expense-tracker-history .expense-history-header h3');
  if (!headerElement) return;
  
  // Get the date range from the inputs
  const fromInput = document.getElementById('expense-tracker-history-from');
  const toInput = document.getElementById('expense-tracker-history-to');
  
  let dateRangeText = '';
  if (fromInput && toInput && fromInput.value && toInput.value) {
    const fromDate = new Date(fromInput.value);
    const toDate = new Date(toInput.value);
    
    // Calculate the number of days
    const timeDiff = toDate.getTime() - fromDate.getTime();
    const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24)) + 1; // +1 to include both start and end dates
    
    // Format the date range text
    if (daysDiff === 1) {
      dateRangeText = ` (${fromDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })})`;
    } else if (daysDiff <= 31) {
      dateRangeText = ` (last ${daysDiff} days)`;
    } else {
      // For longer ranges, show the actual date range
      const fromFormatted = fromDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      const toFormatted = toDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      if (fromDate.getFullYear() !== toDate.getFullYear()) {
        dateRangeText = ` (${fromDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} - ${toDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })})`;
      } else {
        dateRangeText = ` (${fromFormatted} - ${toFormatted})`;
      }
    }
  }
  
  if (filterCategory) {
    // Get the display name for the category
    let displayName = filterCategory;
    
    // If filterCategory is a categoryId (number), get the actual category name
    if (!isNaN(filterCategory)) {
      const budgetData = getCurrentBudgetData();
      if (budgetData && budgetData.categories && budgetData.categories[filterCategory]) {
        const category = budgetData.categories[filterCategory];
        if (category && category.name) {
          displayName = category.name.replace(/^[^\w\s]+\s*/, '').trim() || category.name;
        }
      }
    }
    
    headerElement.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="vertical-align:-2px;margin-right:5px"><path d="M13.4 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-7.4"/><path d="M2 6h4"/><path d="M2 10h4"/><path d="M2 14h4"/><path d="M2 18h4"/><path d="M21.378 5.626a1 1 0 1 0-3.004-3.004l-5.01 5.012a2 2 0 0 0-.506.854l-.837 2.87a.5.5 0 0 0 .62.62l2.87-.837a2 2 0 0 0 .854-.506z"/></svg>Journal Entries - ${displayName}${dateRangeText} <button class="clear-filter-btn" onclick="clearExpenseTrackerHistoryFilter()" style="margin-left: 8px; font-size: 12px; padding: 2px 8px;">Clear</button>`;
  } else {
    headerElement.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="vertical-align:-2px;margin-right:5px"><path d="M13.4 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-7.4"/><path d="M2 6h4"/><path d="M2 10h4"/><path d="M2 14h4"/><path d="M2 18h4"/><path d="M21.378 5.626a1 1 0 1 0-3.004-3.004l-5.01 5.012a2 2 0 0 0-.506.854l-.837 2.87a.5.5 0 0 0 .62.62l2.87-.837a2 2 0 0 0 .854-.506z"/></svg>Journal Entries${dateRangeText}`;
  }
}

/**
 * Clear expense tracker history filter
 */
export function clearExpenseTrackerHistoryFilter() {
  loadExpenseTrackerHistory(); // Reload without filter
}

/**
 * Filter expense tracker history by category
 */
export function filterExpenseTrackerHistoryByCategory(categoryName) {
  loadExpenseTrackerHistory(categoryName);
}

/**
 * Edit expense from tracker history - inline editing
 */
export async function editExpenseFromTrackerHistory(expenseId) {
  console.log('Editing expense from tracker history:', expenseId);

  const expenseItem = document.querySelector(`[data-expense-id="${expenseId}"]`);
  if (!expenseItem) {
    console.error('Expense item not found');
    return;
  }

  // Check if already in edit mode
  if (expenseItem.classList.contains('editing')) {
    return;
  }

  // Get current values from DOM first (before async operations)
  const descriptionEl = expenseItem.querySelector('.expense-item-description');
  const categoryEl = expenseItem.querySelector('.expense-item-category');
  const amountEl = expenseItem.querySelector('.expense-item-amount');

  if (!descriptionEl || !categoryEl || !amountEl) {
    console.error('Could not find expense item elements');
    return;
  }

  const currentLineItem = descriptionEl.textContent.replace('↩ Return', '').trim();
  const currentDescription = categoryEl.textContent || '';
  const currentAmount = amountEl.textContent.replace(/[$,]/g, '');

  // Fetch full expense data from Firestore to get isReturn status
  let isReturn = false;
  try {
    const user = getCurrentUser();
    if (!user) throw new Error('No user authenticated');

    // Check if user is linked to another account
    const linkState = getLinkingState();
    const targetUserId = linkState.isLinkedAccount ? linkState.linkedAccountId : user.uid;

    const expenseRef = doc(db, `users/${targetUserId}/expenses`, expenseId);
    const expenseDoc = await getDoc(expenseRef);

    if (expenseDoc.exists()) {
      const expense = expenseDoc.data();
      isReturn = expense.isReturn === true;
    }
  } catch (error) {
    console.error('Error fetching expense for isReturn status:', error);
    // Continue with isReturn = false
  }

  // Create edit form
  const editForm = `
    <div class="expense-edit-form">
        <div class="edit-row">
          <label>Line Item:</label>
          <input type="text" class="edit-line-item" value="${currentLineItem}" />
        </div>
        <div class="edit-row">
          <label>Description:</label>
          <input type="text" class="edit-description" value="${currentDescription}" placeholder="Optional description" />
        </div>
        <div class="edit-row">
          <label>Amount:</label>
          <input type="number" class="edit-amount" value="${currentAmount}" step="0.01" min="0" />
        </div>
        <div class="edit-row">
          <label>
            <input type="checkbox" class="edit-is-return" ${isReturn ? 'checked' : ''} />
            Return
          </label>
        </div>
        <div class="edit-actions">
          <button class="edit-save-btn" onclick="saveExpenseEdit('${expenseId}')">Save</button>
          <button class="edit-cancel-btn" onclick="cancelExpenseEdit('${expenseId}')">Cancel</button>
        </div>
    </div>
  `;

  // Hide original content and show edit form
  const infoDiv = expenseItem.querySelector('.expense-item-info');
  const originalContent = infoDiv.innerHTML;

  // Store original content for cancel functionality
  expenseItem.setAttribute('data-original-content', originalContent);
  expenseItem.classList.add('editing');

  infoDiv.innerHTML = editForm;

  // Focus on the first input
  const firstInput = infoDiv.querySelector('.edit-line-item');
  if (firstInput) {
    firstInput.focus();
    firstInput.select();
  }
}

/**
 * Save inline expense edit
 */
export async function saveExpenseEdit(expenseId) {
  console.log('Saving expense edit:', expenseId);

  try {
    const expenseItem = document.querySelector(`[data-expense-id="${expenseId}"]`);
    if (!expenseItem) return;

    // Get new values from form
    const lineItem = expenseItem.querySelector('.edit-line-item').value.trim();
    const description = expenseItem.querySelector('.edit-description').value.trim();
    const amount = parseAmountInput(expenseItem.querySelector('.edit-amount').value);
    const isReturn = expenseItem.querySelector('.edit-is-return')?.checked || false;

    // Validate
    if (!lineItem) {
      alert('Line item is required');
      return;
    }

    if (isNaN(amount) || amount <= 0) {
      alert('Please enter a valid amount');
      return;
    }

    // Get current user
    const user = getCurrentUser();
    if (!user) {
      throw new Error('No user authenticated');
    }

    // Update in Firestore — use owner's UID if viewing a linked account
    const linkState = getLinkingState();
    const targetUserId = linkState.isLinkedAccount ? linkState.linkedAccountId : user.uid;
    const expenseRef = doc(db, `users/${targetUserId}/expenses`, expenseId);
    await updateDoc(expenseRef, {
      lineItemName: lineItem,
      description: description || '',
      amount: amount,
      isReturn: isReturn,
      updatedAt: new Date()
    });
    
    // Clear expense cache to ensure fresh data on next load
    if (typeof window !== 'undefined' && window.clearExpenseCache) {
      window.clearExpenseCache();
    }
    
    // Exit edit mode and reload
    expenseItem.classList.remove('editing');
    expenseItem.removeAttribute('data-original-content');

    // Reload expense history
    await loadExpenseTrackerHistory();

    // Refresh expense tracker displays
    await updateOverallProgress();
    await updateCategoryProgress(expenseTrackerState.selectedCategory);
    await loadCategoryExpenseDetails();

    // Refresh remaining amount display if line item is selected
    if (expenseTrackerState.selectedLineItem) {
      showBudgetRemainingInfo();
    }

    // Refresh other views if needed
    if (window.refreshAllProgressData) {
      await window.refreshAllProgressData();
    }

    console.log('Expense updated successfully');
    
  } catch (error) {
    console.error('Error saving expense edit:', error);
    alert('Failed to save changes. Please try again.');
  }
}

/**
 * Cancel inline expense edit
 */
export function cancelExpenseEdit(expenseId) {
  const expenseItem = document.querySelector(`[data-expense-id="${expenseId}"]`);
  if (!expenseItem) return;
  
  // Restore original content
  const originalContent = expenseItem.getAttribute('data-original-content');
  const infoDiv = expenseItem.querySelector('.expense-item-info');
  
  if (originalContent && infoDiv) {
    infoDiv.innerHTML = originalContent;
  }
  
  // Exit edit mode
  expenseItem.classList.remove('editing');
  expenseItem.removeAttribute('data-original-content');
}

/**
 * Delete expense from tracker history
 */
export async function deleteExpenseFromTrackerHistory(expenseId) {
  console.log('Deleting expense from tracker history:', expenseId);
  
  try {
    // Show confirmation dialog
    if (!confirm('Are you sure you want to delete this expense? This action cannot be undone.')) {
      return;
    }
    
    // Get current user
    const user = getCurrentUser();
    if (!user) {
      throw new Error('No user authenticated');
    }
    
    // Use shared account logic
    const linkState = getLinkingState();
    const targetUserId = linkState.isLinkedAccount ? linkState.linkedAccountId : user.uid;
    
    // Delete expense from Firestore (correct path: users/{userId}/expenses/{expenseId})
    const expenseRef = doc(db, `users/${targetUserId}/expenses`, expenseId);
    await deleteDoc(expenseRef);
    
    // Clear expense cache to ensure fresh data on next load
    if (typeof window !== 'undefined' && window.clearExpenseCache) {
      window.clearExpenseCache();
    }

    // Reload expense history
    await loadExpenseTrackerHistory();

    // Refresh expense tracker displays
    await updateOverallProgress();
    await updateCategoryProgress(expenseTrackerState.selectedCategory);
    await loadCategoryExpenseDetails();

    // Refresh remaining amount display if line item is selected
    if (expenseTrackerState.selectedLineItem) {
      showBudgetRemainingInfo();
    }

    // Refresh other views if needed
    if (window.refreshAllProgressData) {
      await window.refreshAllProgressData();
    }

    console.log('Expense deleted successfully from tracker history');
    
  } catch (error) {
    console.error('Error deleting expense from tracker history:', error);
    alert('Failed to delete expense. Please try again.');
  }
}

// Export deleteExpense function (other functions already exported above)
export { deleteExpense };

// Clear all in-memory expense tracker state on logout / user switch
export function clearExpenseTrackerState() {
  expenseTrackerState.currentBudgetData = null;
  expenseTrackerState.monthlyTotals = null;
  expenseTrackerState.selectedCategory = null;
  expenseTrackerState.selectedLineItem = null;
  expenseTrackerState.recentExpenses = [];
  expenseTrackerState.upcomingRecurring = [];
  console.log('Expense tracker state cleared');
}
